import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  Polyline,
  Polygon,
  Circle,
  OverlayView,
} from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Checkbox,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import CTLNotification from "application/components/CTLNotification";
import loggerFactory from "globals/logger/logger-factory";
import MAPActionButton from "./MAPActionButton";
import SearchBar from "./MAPSearchBar";
import DirectionsInfoBox from "./DirectionsInfoBox";
import MarkerPopup from "./MarkerPopup";

import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MapIcon from "@mui/icons-material/Map";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PentagonIcon from "@mui/icons-material/Pentagon";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import NotificationManager from "globals/websocket/WebSocket";
import { telemetry } from "globals/endpoints";
import MonitoringPill from "./MonitoringPill";
import UserRoute from "./MarkerPopup/userRoute";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  useGetAllGeofencesQuery,
  useUpdateGeofenceMutation,
} from "features/geofenceSlice";
import { clearGeofenceRedraw } from "features/geofenceUiSlice";
import GeofenceEditorDialog from "application/modules/Dispatcher/GeofencePanel/GeofenceEditorDialog";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import CheckIcon from "@mui/icons-material/Check";
const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 28.6139,
  lng: 77.209,
};

const mapStyles = [
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.attraction",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "poi.medical",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.school",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.park",
    stylers: [{ visibility: "on" }],
  },
  {
    featureType: "landscape.natural",
    stylers: [{ visibility: "on" }],
  },
];

const mapStylesWithoutLabels = [
  ...mapStyles,
  {
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
];

const libraries = ["geometry", "places", "visualization", "directions"];

const USER_ROUTE_MARKER_SIZE = 38;
const USER_ROUTE_MIN_SEGMENT_DURATION = 120;
const USER_ROUTE_MAX_SEGMENT_DURATION = 680;
// SVG arrow path pointing north — rotated via `rotation` field for smooth 360° heading
const USER_ROUTE_FIT_BOUNDS_PADDING = 90;
const USER_ROUTE_DUPLICATE_DISTANCE_METERS = 2.5;
const USER_ROUTE_TINY_MOVE_DISTANCE_METERS = 1.4;
const USER_ROUTE_SPIKE_JUMP_METERS = 85;
const USER_ROUTE_SPIKE_RETURN_METERS = 24;
const USER_ROUTE_MAX_SPEED_MPS = 42;
const USER_ROUTE_SUDDEN_JUMP_METERS = 20;
const USER_ROUTE_SUDDEN_JUMP_MAX_SECONDS = 10;
const USER_ROUTE_SUDDEN_JUMP_RETURN_METERS = 32;
const USER_ROUTE_SUDDEN_JUMP_RETURN_WINDOW = 3;

function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function lerpAngle(fromDeg, toDeg, t) {
  const diff = ((toDeg - fromDeg + 540) % 360) - 180;
  return fromDeg + diff * t;
}

function getDistanceMeters(pointA, pointB) {
  if (!pointA || !pointB) {
    return 0;
  }

  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRadians(pointB.lat - pointA.lat);
  const dLng = toRadians(pointB.lng - pointA.lng);
  const lat1 = toRadians(pointA.lat);
  const lat2 = toRadians(pointB.lat);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return 2 * earthRadius * Math.asin(Math.sqrt(a));
}

const MapComponent = ({
  searchText,
  color,
  showArea,
  showRoute,
  setShowArea,
  setShowRoute,
  routeData,
  areaData,
  data,
  clickToMessageMarker,
  dragCallTransfer,
  departmentList,
  dragToCall,
  onAreaCreated,
  handleClearEmergency,
  mapCenterTarget,
  defaultShowSearch = false,
  isDisable = false,
  locationUpdateColor = "warn",
  onRecordingPage,
  onPage,
  onDial,
  onGroupCall,
  onLiveGroupCall,
  onMessage,
  onEmergency,
  onNavigateHome,
  onAnchorLocation,
  overlayActionPanel,
  onBoundsChanged,
  onContactAction,
}) => {
  const componentName = "Map";

  const [selectedMarker, setSelectedMarker] = useState(null);

  const [mapRef, setMapRef] = useState(null);
  const clusterRef = useRef(null);
  const markersRef = useRef([]);
  const originalPositionsRef = useRef({});
  const dragStartRef = useRef(null);
  // Mini popups shown while dragging (info/blue) and on marker hover (success/green)
  const dragMiniPopupRef = useRef(null);
  const hoverMiniPopupRef = useRef(null);
  // Popup for the marker currently under the dragged marker (success/green)
  const dropTargetPopupRef = useRef(null);
  const dropTargetRef = useRef(null);

  const [currentLocation, setCurrentLocation] = useState(center);
  const [directions, setDirections] = useState(null);
  const [destination, setDestination] = useState("");
  const [showSearch, setShowSearch] = useState(Boolean(defaultShowSearch));

  const mapDefaultZoom =
    Number(AuthenticationService.getMapDefaultZoom()) || 16;
  const [zoomLevel, setZoomLevel] = useState(mapDefaultZoom);
  const [mapCenter, setMapCenter] = useState(currentLocation);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const nodeRef = useRef(null);

  const [userPaths, setUserPaths] = useState([]);
  const [hoveredUser, setHoveredUser] = useState(null);

  const [map, setMap] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [mapTypeId, setMapTypeId] = useState("satellite");
  const [mapLabelsEnabled, setMapLabelsEnabled] = useState(true);
  const [selectedShape, setSelectedShape] = useState(null);
  const [drawingMode, setDrawingMode] = useState(false);

  // Geofence draw tool - Google removed DrawingManager from the Maps
  // JavaScript API (v3.65+), so this is a custom tap/click-to-place-point
  // tool built on raw map events instead. Kept fully separate from the
  // manual rectangle drag-draw tool above (unrelated broadcast-call area
  // select feature).
  const [geofenceEditorOpen, setGeofenceEditorOpen] = useState(false);
  const [pendingGeofenceShape, setPendingGeofenceShape] = useState(null);
  const [editingGeofence, setEditingGeofence] = useState(null);
  const [geofenceDrawShapeType, setGeofenceDrawShapeType] = useState(null); // null | "polygon" | "circle" | "rectangle"
  const [geofenceDrawPoints, setGeofenceDrawPoints] = useState([]); // [{lat, lng}, ...] plain numbers
  const [geofenceMenuAnchor, setGeofenceMenuAnchor] = useState(null); // anchor el for the "Create Geofence" shape picker
  const [pendingPreviewColor, setPendingPreviewColor] = useState("#3388ff"); // live color of the not-yet-saved fence rendered while the editor is open
  const pendingGeofenceOverlayRef = useRef(null);
  const { data: geofenceListData } = useGetAllGeofencesQuery();

  // Redraw ("resize") of an existing fence, triggered from the geofence panel.
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const redrawGeofence = useSelector((state) => state.geofenceUi.redrawGeofence);
  const [updateGeofence] = useUpdateGeofenceMutation();
  const redrawTargetRef = useRef(null);

  const clearPendingGeofenceOverlay = () => {
    pendingGeofenceOverlayRef.current?.setMap?.(null);
    pendingGeofenceOverlayRef.current = null;
  };

  // Called by every "finish draw" path. In redraw mode it saves the new
  // geometry onto the existing fence (keeping name/color/assignments);
  // otherwise it opens the create editor as before.
  const completeGeofenceShape = (shapePayload) => {
    const target = redrawTargetRef.current;
    if (target) {
      redrawTargetRef.current = null;
      dispatch(clearGeofenceRedraw());
      setGeofenceDrawShapeType(null);
      setGeofenceDrawPoints([]);
      updateGeofence({
        id: target.id,
        name: target.name,
        color: target.color,
        shapeType: shapePayload.shapeType,
        coordinates: shapePayload.coordinates,
        centerLat: shapePayload.centerLat,
        centerLng: shapePayload.centerLng,
        radiusMeters: shapePayload.radiusMeters,
        assignedUsers: target.assignedUsers,
        assignedDepartments: target.assignedDepartments,
      })
        .unwrap()
        .then(() => enqueueSnackbar("Geofence shape updated", { variant: "success" }))
        .catch(() =>
          enqueueSnackbar("Unable to update geofence shape", { variant: "error" }),
        );
      return;
    }

    setEditingGeofence(null);
    setPendingGeofenceShape(shapePayload);
    setGeofenceEditorOpen(true);
    setGeofenceDrawShapeType(null);
  };

  const handleGeofenceColorPreview = (color) => {
    setPendingPreviewColor(color);
    pendingGeofenceOverlayRef.current?.setOptions?.({
      strokeColor: color,
      fillColor: color,
    });
  };

  const startGeofenceDraw = (shapeType) => {
    clearPendingGeofenceOverlay();
    setEditingGeofence(null);
    setGeofenceDrawPoints([]);
    setPendingPreviewColor("#3388ff");
    setGeofenceDrawShapeType(shapeType);
  };

  const cancelGeofenceDraw = () => {
    setGeofenceDrawShapeType(null);
    setGeofenceDrawPoints([]);
    if (redrawTargetRef.current) {
      redrawTargetRef.current = null;
      dispatch(clearGeofenceRedraw());
    }
  };

  // When the panel requests a resize/redraw, enter draw mode for that fence's
  // shape type; completeGeofenceShape() then saves onto the existing fence.
  useEffect(() => {
    if (!redrawGeofence || !map || !googleMapsLoaded) return;
    redrawTargetRef.current = redrawGeofence;
    const shape = (redrawGeofence.shapeType || "POLYGON").toLowerCase();
    startGeofenceDraw(
      shape === "circle" ? "circle" : shape === "rectangle" ? "rectangle" : "polygon",
    );
    // eslint-disable-next-line
  }, [redrawGeofence, map, googleMapsLoaded]);

  const undoGeofenceDrawPoint = () => {
    setGeofenceDrawPoints((prev) => prev.slice(0, -1));
  };

  const finishGeofencePolygonDraw = () => {
    setGeofenceDrawPoints((prev) => {
      if (prev.length < 3) return prev;

      completeGeofenceShape({
        shapeType: "POLYGON",
        coordinates: prev.map((point) => ({
          lat: String(point.lat),
          lng: String(point.lng),
        })),
      });
      return [];
    });
  };

  const finishGeofenceCircleFromPoints = (points) => {
    const [centerPoint, edgePoint] = points;
    const radiusMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
      new window.google.maps.LatLng(centerPoint.lat, centerPoint.lng),
      new window.google.maps.LatLng(edgePoint.lat, edgePoint.lng),
    );

    completeGeofenceShape({
      shapeType: "CIRCLE",
      centerLat: String(centerPoint.lat),
      centerLng: String(centerPoint.lng),
      radiusMeters,
    });
  };

  const finishGeofenceRectangleFromPoints = (points) => {
    const [firstCorner, secondCorner] = points;
    const north = Math.max(firstCorner.lat, secondCorner.lat);
    const south = Math.min(firstCorner.lat, secondCorner.lat);
    const east = Math.max(firstCorner.lng, secondCorner.lng);
    const west = Math.min(firstCorner.lng, secondCorner.lng);

    completeGeofenceShape({
      shapeType: "RECTANGLE",
      coordinates: [
        { lat: String(north), lng: String(east) },
        { lat: String(south), lng: String(east) },
        { lat: String(south), lng: String(west) },
        { lat: String(north), lng: String(west) },
      ],
    });
  };

  // Circle and rectangle no longer auto-open the editor at 2 points; the user
  // reviews the drawn shape and clicks Finish (same as polygon).
  const finishGeofenceDraw = () => {
    if (geofenceDrawShapeType === "polygon") {
      finishGeofencePolygonDraw();
      return;
    }
    if (geofenceDrawPoints.length < 2) return;
    if (geofenceDrawShapeType === "circle") {
      finishGeofenceCircleFromPoints(geofenceDrawPoints);
    } else if (geofenceDrawShapeType === "rectangle") {
      finishGeofenceRectangleFromPoints(geofenceDrawPoints);
    }
    setGeofenceDrawPoints([]);
  };

  const handleGeofenceDrawPoint = (point) => {
    setGeofenceDrawPoints((prev) => {
      // Circle and rectangle are defined by exactly two points; ignore extra
      // taps until the user hits Finish (or Undo to re-place a point).
      if (
        (geofenceDrawShapeType === "circle" ||
          geofenceDrawShapeType === "rectangle") &&
        prev.length >= 2
      ) {
        return prev;
      }
      return [...prev, point];
    });
  };

  // Tap/click-to-place-point drawing - works with mouse (via the map's
  // native "click" event) and touch (via native touchstart/touchend on the
  // map's DOM node, distinguishing a tap from a pan by movement + duration),
  // following the same mouse/touch-dual-handling approach already used by
  // the drag-rectangle broadcast-call tool above.
  useEffect(() => {
    if (!map || !googleMapsLoaded || !geofenceDrawShapeType) {
      return undefined;
    }

    let touchActive = false;
    let touchStart = null;
    const TAP_MOVE_THRESHOLD_PX = 12;
    const TAP_MAX_DURATION_MS = 600;

    const pixelToLatLng = (clientX, clientY) => {
      const container = map.getDiv();
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const bounds = map.getBounds();
      if (!bounds) return null;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const lat = sw.lat() + (ne.lat() - sw.lat()) * (1 - y / rect.height);
      const lng = sw.lng() + (ne.lng() - sw.lng()) * (x / rect.width);
      return new window.google.maps.LatLng(lat, lng);
    };

    const addPointFromLatLng = (latLng) => {
      if (!latLng) return;
      handleGeofenceDrawPoint({ lat: latLng.lat(), lng: latLng.lng() });
    };

    const clickListener = map.addListener("click", (e) => {
      if (touchActive) return;
      addPointFromLatLng(e.latLng);
    });

    const mapDiv = map.getDiv();

    // Taps that land on the geofence control overlay (Undo / Finish / Cancel,
    // shape picker, etc.) must NOT be treated as map taps - otherwise the map's
    // touchend handler calls preventDefault() and swallows the button's click.
    const isOnGeofenceControl = (target) =>
      !!(target && target.closest && target.closest("[data-geofence-control]"));

    const handleTouchStart = (e) => {
      if (isOnGeofenceControl(e.target)) return;
      if (e.touches.length !== 1) return;
      touchActive = true;
      const touch = e.touches[0];
      touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    };

    const handleTouchEnd = (e) => {
      if (isOnGeofenceControl(e.target)) {
        touchActive = false;
        touchStart = null;
        return;
      }
      const startPoint = touchStart;
      const endTouch = e.changedTouches && e.changedTouches[0];
      touchActive = false;
      touchStart = null;
      if (!startPoint || !endTouch) return;

      const dx = endTouch.clientX - startPoint.x;
      const dy = endTouch.clientY - startPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = Date.now() - startPoint.time;

      if (distance <= TAP_MOVE_THRESHOLD_PX && duration <= TAP_MAX_DURATION_MS) {
        e.preventDefault();
        addPointFromLatLng(pixelToLatLng(endTouch.clientX, endTouch.clientY));
      }
    };

    const handleTouchCancel = () => {
      touchActive = false;
      touchStart = null;
    };

    mapDiv.addEventListener("touchstart", handleTouchStart, { passive: true });
    mapDiv.addEventListener("touchend", handleTouchEnd, { passive: false });
    mapDiv.addEventListener("touchcancel", handleTouchCancel, { passive: true });
    map.setOptions({ draggableCursor: "crosshair" });

    return () => {
      window.google.maps.event.removeListener(clickListener);
      mapDiv.removeEventListener("touchstart", handleTouchStart);
      mapDiv.removeEventListener("touchend", handleTouchEnd);
      mapDiv.removeEventListener("touchcancel", handleTouchCancel);
      map.setOptions({ draggableCursor: null });
    };
    // eslint-disable-next-line
  }, [map, googleMapsLoaded, geofenceDrawShapeType]);

  const [isEditing, setIsEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [routeDirections, setRouteDirections] = useState({});

  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [showRouteList, setShowRouteList] = useState(false);

  const currentRectRef = useRef(null);
  const selectedShapeRef = useRef(null);

  const [markerData, setMarkerData] = useState(data);

  const [sortedNearbyMarkers, setSortedNearbyMarkers] = useState([]);
  const [selectedDropdownMarker, setSelectedDropdownMarker] = useState(null);
  const [routeList, setRouteList] = useState(routeData);
  const [loading, setLoading] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const liveUserRef = useRef(null);
  const [liveUser, setLiveUser] = useState(null);
  const [openUserRoute, setOpenUserRoute] = useState(false);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    if (data) {
      setMarkerData(data);
    }
  }, [data]);

  useEffect(() => {
    setShowSearch(Boolean(defaultShowSearch));
  }, [defaultShowSearch]);

  useEffect(() => {
    if (routeData) {
      setRouteList(routeData);
    }
  }, [routeData]);

  const applyTinyMapControls = (mapInstance) => {
    const mapContainer = mapInstance?.getDiv?.();

    if (!mapContainer) {
      return;
    }

    const currentMapType = mapInstance?.getMapTypeId?.();
    const isSatelliteControlTheme =
      currentMapType === "satellite" || currentMapType === "hybrid";

    const controlBg = isSatelliteControlTheme
      ? "rgba(24, 27, 32, 0.84)"
      : "rgba(255, 255, 255, 0.92)";
    const controlText = isSatelliteControlTheme
      ? "rgba(255, 255, 255, 0.94)"
      : "rgba(23, 31, 43, 0.92)";
    const controlBorder = isSatelliteControlTheme
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(18, 66, 133, 0.24)";
    const controlShadow = isSatelliteControlTheme
      ? "0 5px 14px rgba(0, 0, 0, 0.42)"
      : "0 5px 14px rgba(15, 23, 42, 0.16)";

    // Shrink default Google controls beyond controlSize limits.
    mapContainer
      .querySelectorAll(
        ".gm-bundled-control, .gm-fullscreen-control, .gm-style-mtc",
      )
      .forEach((element) => {
        element.style.transform = "scale(0.8)";
        element.style.transformOrigin = "top left";
        element.style.margin = "3px";
        element.style.filter = "none";
      });

    // Make MAP/Satellite type switch compact.
    const mapTypeControl = mapContainer.querySelector(".gm-style-mtc");
    if (mapTypeControl) {
      mapTypeControl.style.transform = "scale(0.72)";
      mapTypeControl.style.transformOrigin = "top left";
      mapTypeControl.style.margin = "2px";
    }

    mapContainer.querySelectorAll(".gm-style-mtc button").forEach((button) => {
      button.style.fontSize = "9px";
      button.style.minHeight = "20px";
      button.style.padding = "0 4px";
      button.style.lineHeight = "1.1";
    });

    // Apply visual theme to Google native controls.
    mapContainer
      .querySelectorAll(
        ".gm-control-active, .gm-fullscreen-control button, .gm-style-mtc button",
      )
      .forEach((button) => {
        button.style.background = controlBg;
        button.style.color = controlText;
        button.style.border = controlBorder;
        button.style.borderRadius = "10px";
        button.style.boxShadow = controlShadow;
        button.style.backdropFilter = "blur(5px)";
        button.style.padding = "3px";
        button.style.minWidth = "28px";
        button.style.minHeight = "28px";
        button.style.transition = "all 0.2s ease";
      });

    // Street View (Pegman) control wrapper + button surface theming.
    mapContainer
      .querySelectorAll(
        ".gm-svpc, .gm-svpc > div, .gm-svpc button, .gm-svpc .gm-control-active",
      )
      .forEach((node) => {
        if (!node?.style) {
          return;
        }
        node.style.background = controlBg;
        node.style.border = controlBorder;
        node.style.borderRadius = "10px";
        node.style.boxShadow = controlShadow;
        node.style.backdropFilter = "blur(5px)";
      });

    // Keep native Google control glyphs untouched so +/- remain visible on hover.
    mapContainer
      .querySelectorAll(".gm-control-active img, .gm-control-active svg")
      .forEach((iconNode) => {
        if (iconNode?.style) {
          iconNode.style.filter = "none";
          iconNode.style.opacity = "1";
        }
      });
  };

  const normalizedMarkers = useMemo(
    () =>
      (markerData || [])
        .map((item) => {
          const latitude = Number.parseFloat(String(item?.geoX || "").trim());
          const longitude = Number.parseFloat(String(item?.geoY || "").trim());

          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return null;
          }

          return {
            ...item,
            _lat: latitude,
            _lng: longitude,
          };
        })
        .filter(Boolean),
    [markerData],
  );

  const filteredList = useMemo(() => {
    const query = String(searchText || "").toLowerCase().trim();

    if (query) {
      return normalizedMarkers.filter(
        (item) =>
          String(item?.name || "").toLowerCase().includes(query) ||
          String(item?.designation || "").toLowerCase().includes(query) ||
          String(item?.extensionNumber || "").toLowerCase().includes(query),
      );
    }

    if (userPaths?.[0]?.userName) {
      const userValue = String(userPaths[0].userName || "");
      return normalizedMarkers.filter((item) =>
        String(item?.extensionNumber || "").includes(userValue),
      );
    }

    return normalizedMarkers;
  }, [normalizedMarkers, searchText, userPaths]);

  const areMarkersSame = (previousMarkers, nextMarkers) => {
    if (!previousMarkers || !nextMarkers) {
      return false;
    }

    if (previousMarkers.length !== nextMarkers.length) {
      return false;
    }

    for (let index = 0; index < previousMarkers.length; index += 1) {
      const prev = previousMarkers[index];
      const next = nextMarkers[index];

      if (
        prev?.userCoordinateId !== next?.userCoordinateId ||
        prev?.emergencyStatus !== next?.emergencyStatus ||
        prev?.geoX !== next?.geoX ||
        prev?.geoY !== next?.geoY
      ) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    if (!map || !googleMapsLoaded) {
      setVisibleMarkers(filteredList);
      return;
    }

    if (isMonitoring && liveUserRef.current) {
      setVisibleMarkers([liveUserRef.current]);
      return;
    }

    const computeVisibleMarkers = () => {
      const bounds = map.getBounds();

      if (!bounds) {
        setVisibleMarkers(filteredList);
        return;
      }

      if (onBoundsChanged) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        onBoundsChanged({
          neLat: ne.lat(),
          neLng: ne.lng(),
          swLat: sw.lat(),
          swLng: sw.lng(),
        });
      }

      const nextVisible = filteredList.filter((item) => {
        const point = new window.google.maps.LatLng(item._lat, item._lng);
        return bounds.contains(point);
      });

      const boundedList = nextVisible.length ? nextVisible : filteredList;

      setVisibleMarkers((previous) =>
        areMarkersSame(previous, boundedList) ? previous : boundedList,
      );
    };

    computeVisibleMarkers();

    const idleListener = map.addListener("idle", computeVisibleMarkers);

    return () => {
      if (idleListener) {
        window.google.maps.event.removeListener(idleListener);
      }
    };
  }, [filteredList, googleMapsLoaded, isMonitoring, map]);
  const handleLiveMonitoringUser = (data) => {
    if (!data) return;
    const message = JSON.parse(data.body);
    loggerFactory.info("MAP", "Event get from socket", message);
    if (
      liveUserRef.current &&
      liveUserRef.current.deviceId == message.deviceId
    ) {
      const latestLocation = message.location?.[0] || {};
      const updatedMarker = {
        ...liveUserRef.current,
        geoX: latestLocation.latitude || liveUserRef.current.geoX,
        geoY: latestLocation.longitude || liveUserRef.current.geoY,
        batteryPercentage:
          message.batteryPercentage ?? liveUserRef.current.batteryPercentage,
        updatedOn: latestLocation.epochDate ?? liveUserRef.current.updatedOn,
        gpsAccuracy:
          latestLocation.gpsAccuracy ?? liveUserRef.current.gpsAccuracy,
        rsRpSim1: message.rsrpData?.sim1 ?? liveUserRef.current.rsRpSim1,
        rsRpSim2: message.rsrpData?.sim2 ?? liveUserRef.current.rsRpSim2,
      };
      setVisibleMarkers([updatedMarker]);
    } else {
      loggerFactory.info("MAP", "Live User not found to update location.");
    }
  };

  useEffect(() => {
    NotificationManager.receiveMessageForRouteCoordinate(
      "routeCoordinate/live",
      handleLiveMonitoringUser,
      "telemetry-events",
    );
  }, []);

  const handleLiveMonitoring = async (data) => {
    setLoading(true);
    try {
      await telemetry.post(`routeCoordinate/monitor/${data.designation}`);
      setIsMonitoring(true);
      liveUserRef.current = data;
      setVisibleMarkers([data]);
      setNotify({
        isOpen: true,
        status: "Live monitoring started",
        type: "success",
        pagename: componentName,
      });
    } catch (err) {
      setNotify({
        isOpen: true,
        message: err?.response?.data?.message,
        type: "success",
        pagename: componentName,
        status: "Failed to start live monitoring",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearMonitoring = async () => {
    setLoading(true);
    try {
      await telemetry.post("routeCoordinate/clear-monitor");
      setIsMonitoring(false);
      liveUserRef.current = null;
      setVisibleMarkers(filteredList);
      setNotify({
        isOpen: true,
        status: "Live monitoring Stop",
        type: "success",
        pagename: componentName,
      });
    } catch (err) {
      setNotify({
        isOpen: true,
        status: "Failed to clear live monitoring",
        type: "error",
        pagename: componentName,
        message: err?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!map || !googleMapsLoaded || !drawingMode) {
      return;
    }

    let startLatLng = null;
    let isDrawing = false;
    let completed = false;
    let touchActive = false; // true while a finger is on screen — blocks Google Maps synthetic mouse events

    map.setOptions({ draggable: false });

    const pixelToLatLng = (clientX, clientY) => {
      const container = map.getDiv();
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const bounds = map.getBounds();
      if (!bounds) return null;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const lat = sw.lat() + (ne.lat() - sw.lat()) * (1 - y / rect.height);
      const lng = sw.lng() + (ne.lng() - sw.lng()) * (x / rect.width);
      return new window.google.maps.LatLng(lat, lng);
    };

    const startDraw = (latLng) => {
      isDrawing = true;
      startLatLng = latLng;

      if (selectedShapeRef.current) {
        selectedShapeRef.current.setMap(null);
        setSelectedShape(null);
        selectedShapeRef.current = null;
      }
      if (currentRectRef.current) {
        currentRectRef.current.setMap(null);
        currentRectRef.current = null;
      }

      currentRectRef.current = new window.google.maps.Rectangle({
        map,
        bounds: new window.google.maps.LatLngBounds(
          { lat: latLng.lat(), lng: latLng.lng() },
          { lat: latLng.lat(), lng: latLng.lng() },
        ),
        fillColor: color,
        fillOpacity: 0.5,
        strokeWeight: 2,
        clickable: false,
        editable: false,
        draggable: false,
      });
    };

    const updateDraw = (latLng) => {
      if (!isDrawing || !startLatLng || !currentRectRef.current) return;

      currentRectRef.current.setBounds(
        new window.google.maps.LatLngBounds(
          {
            lat: Math.min(startLatLng.lat(), latLng.lat()),
            lng: Math.min(startLatLng.lng(), latLng.lng()),
          },
          {
            lat: Math.max(startLatLng.lat(), latLng.lat()),
            lng: Math.max(startLatLng.lng(), latLng.lng()),
          },
        ),
      );
    };

    const finishDraw = () => {
      if (!isDrawing || !startLatLng || !currentRectRef.current) return;
      isDrawing = false;
      completed = true;

      const rect = currentRectRef.current;
      rect.type = "rectangle";
      rect.setDraggable(true);
      rect.setOptions({ clickable: true });

      setSelectedShape(rect);

      const coords = rect.getBounds().toJSON();
      loggerFactory.info(
        componentName,
        "Drawn shape bounds/coords:\n",
        JSON.stringify(coords),
      );

      onAreaCreated(coords);
      setDrawingMode(false);
      map.setOptions({ draggableCursor: null, draggable: true });
      startLatLng = null;
    };

    // Mouse listeners — skipped entirely while a touch draw is in progress
    const downListener = map.addListener("mousedown", (e) => {
      if (touchActive) return;
      startDraw(e.latLng);
    });
    const moveListener = map.addListener("mousemove", (e) => {
      if (touchActive) return;
      updateDraw(e.latLng);
    });
    const upListener = map.addListener("mouseup", () => {
      if (touchActive) return; // finger still on screen — wait for touchend
      finishDraw();
    });

    // Touch listeners on the map container DOM node
    const mapDiv = map.getDiv();

    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      touchActive = true;
      const touch = e.touches[0];
      const latLng = pixelToLatLng(touch.clientX, touch.clientY);
      if (latLng) startDraw(latLng);
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const latLng = pixelToLatLng(touch.clientX, touch.clientY);
      if (latLng) updateDraw(latLng);
    };

    // finishDraw is called ONLY here — when the finger is actually lifted
    const handleTouchEnd = (e) => {
      e.preventDefault();
      touchActive = false;
      finishDraw();
    };

    // If touch is cancelled (e.g. phone call interruption) clean up gracefully
    const handleTouchCancel = (e) => {
      e.preventDefault();
      touchActive = false;
      isDrawing = false;
    };

    mapDiv.addEventListener("touchstart", handleTouchStart, { passive: false });
    mapDiv.addEventListener("touchmove", handleTouchMove, { passive: false });
    mapDiv.addEventListener("touchend", handleTouchEnd, { passive: false });
    mapDiv.addEventListener("touchcancel", handleTouchCancel, { passive: false });

    return () => {
      window.google.maps.event.removeListener(downListener);
      window.google.maps.event.removeListener(moveListener);
      window.google.maps.event.removeListener(upListener);
      mapDiv.removeEventListener("touchstart", handleTouchStart);
      mapDiv.removeEventListener("touchmove", handleTouchMove);
      mapDiv.removeEventListener("touchend", handleTouchEnd);
      mapDiv.removeEventListener("touchcancel", handleTouchCancel);
      if (!completed && currentRectRef.current) {
        currentRectRef.current.setMap(null);
        currentRectRef.current = null;
      }
      map.setOptions({ draggable: true });
    };
  }, [map, googleMapsLoaded, drawingMode]);

  useEffect(() => {
    if (!mapCenterTarget) {
      return;
    }

    const latitude = Number(
      Array.isArray(mapCenterTarget)
        ? mapCenterTarget[0]
        : mapCenterTarget?.lat,
    );
    const longitude = Number(
      Array.isArray(mapCenterTarget)
        ? mapCenterTarget[1]
        : mapCenterTarget?.lng,
    );

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    const nextCenter = { lat: latitude, lng: longitude };

    setMapCenter(nextCenter);
    setCurrentLocation(nextCenter);

    if (mapRef?.panTo && typeof mapRef.panTo === "function") {
      mapRef.panTo(nextCenter);
    }

    if (mapRef?.setZoom && typeof mapRef.setZoom === "function") {
      mapRef.setZoom(mapDefaultZoom);
    }
  }, [mapCenterTarget, mapDefaultZoom, mapRef]);

  useEffect(() => {
    if (
      Array.isArray(filteredList) &&
      filteredList.length === 1 &&
      searchText
    ) {
      const position = {
        lat: filteredList[0]._lat,
        lng: filteredList[0]._lng,
      };
      setCurrentLocation(position);
      if (mapRef?.panTo && typeof mapRef.panTo === "function") {
        mapRef.panTo(position);
      }
      if (mapRef?.setZoom && typeof mapRef.setZoom === "function") {
        mapRef.setZoom(zoomLevel);
      }
    }
  }, [filteredList, searchText]);

  useEffect(() => {
    selectedShapeRef.current = selectedShape;
  }, [selectedShape]);

  useEffect(() => {
    if (selectedShape) {
      selectedShape.setOptions({ fillColor: color });
    }
  }, [color]);

  useEffect(() => {
    if (!googleMapsLoaded || !routeList?.length) return;
    routeList.forEach((route) => {
      const coords = route.coordinates
        .map((c) => ({
          lat: parseFloat(c.latitude),
          lng: parseFloat(c.longitude),
        }))
        .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));

      if (coords.length < 2) return;

      const origin = coords[0];
      const destination = coords[coords.length - 1];

      if (
        !routeDirections[route.id] &&
        window.google?.maps?.DirectionsService
      ) {
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
          {
            origin,
            destination,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === "OK") {
              setRouteDirections((prev) => ({
                ...prev,
                [route.id]: {
                  result,
                  origin,
                  destination,
                  name: route.name,
                },
              }));
            }
          },
        );
      }
    });
  }, [googleMapsLoaded, routeList]);

  const getAddressFromLatLng = (lat, lng) => {
    return new Promise((resolve, reject) => {
      if (!window.google) return reject("Google not loaded");

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0].formatted_address);
        } else {
          resolve("Address not found");
        }
      });
    });
  };

  function getDirectionalIcon(heading) {
    if (heading >= -45 && heading <= 45) return "/images/man-east.png";
    if (heading > 45 && heading <= 135) return "/images/man-north.png";
    if (heading < -45 && heading >= -135) return "/images/man-south.png";
    return "/images/man-west.png";
  }

  function getColorByDepartment(department) {
    // Match on the department code OR its description so the color resolves
    // even when `department` (code) and `departmentDescription` (name) differ.
    const departmentInfo =
      departmentList &&
      departmentList.find(
        (item) =>
          item.department === department ||
          item.departmentDescription === department,
      );
    return departmentInfo && departmentInfo.color
      ? departmentInfo.color
      : "gray";
  }

  // ── Mini drag/hover popups ────────────────────────────────────────────────
  const getMarkerName = (item) =>
    item?.designation || item?.name || item?.userName || "—";

  const getMarkerNumber = (item) =>
    item?.extensionNumber ||
    item?.extension ||
    item?.offEXtn ||
    item?.number ||
    "";

  // variant: "drag" => info/blue, "hover" => success/green
  const buildMiniPopupHTML = (item, variant) => {
    const palette =
      variant === "drag"
        ? { bg: "#0288d1", border: "#0277bd" } // info
        : { bg: "#2e9e44", border: "#2e7d32" }; // success
    const name = getMarkerName(item);
    const number = getMarkerNumber(item);
    return `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:${palette.bg};color:#fff;border:1px solid ${palette.border};border-radius:6px;padding:4px 8px;min-width:64px;max-width:190px;box-shadow:0 2px 6px rgba(0,0,0,0.35);line-height:1.25;">
        <div style="font-size:11px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name}</div>
        ${number
        ? `<div style="font-size:10px;opacity:.92;margin-top:1px;white-space:nowrap;">📱 ${number}</div>`
        : ""
      }
      </div>`;
  };

  // Lightweight OverlayView-based popup (full color/size control, no default chrome)
  const createMiniPopup = (mapInstance, position, html) => {
    if (!mapInstance || !window.google?.maps?.OverlayView) return null;

    const overlay = new window.google.maps.OverlayView();
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.transform = "translate(-50%, -140%)";
    div.style.pointerEvents = "none";
    div.style.zIndex = "9999";
    div.innerHTML = html;

    let currentPosition = position;

    const toLatLng = (pos) =>
      pos instanceof window.google.maps.LatLng
        ? pos
        : new window.google.maps.LatLng(pos.lat, pos.lng);

    overlay.onAdd = function () {
      this.getPanes().floatPane.appendChild(div);
    };
    overlay.draw = function () {
      const projection = this.getProjection();
      if (!projection || !currentPosition) return;
      const point = projection.fromLatLngToDivPixel(toLatLng(currentPosition));
      if (point) {
        div.style.left = `${point.x}px`;
        div.style.top = `${point.y}px`;
      }
    };
    overlay.onRemove = function () {
      if (div.parentNode) div.parentNode.removeChild(div);
    };
    overlay.updatePosition = function (nextPosition) {
      currentPosition = nextPosition;
      this.draw();
    };
    overlay.updateContent = function (nextHtml) {
      div.innerHTML = nextHtml;
    };

    overlay.setMap(mapInstance);
    return overlay;
  };

  const clearMiniPopup = (ref) => {
    if (ref.current) {
      ref.current.setMap(null);
      ref.current = null;
    }
  };

  //user movement action create
  useEffect(() => {
    if (!googleMapsLoaded || !map) return;
    const activeIntervals = [];
    const activeMarkers = [];
    if (!selectedRoutes || selectedRoutes.length === 0) {
      loggerFactory.info(componentName, "No route selected. Cleaning up.", "");
      if (activeMarkers.length) {
        activeMarkers.forEach((marker) => marker.setMap(null));
      }
      activeIntervals.forEach(clearInterval);
      return;
    }

    selectedRoutes.forEach((routeName) => {
      const route = Object.values(routeDirections).find(
        (r) => r.name === routeName,
      );

      if (!route?.result?.routes?.[0]?.overview_path) {
        loggerFactory.info(
          componentName,
          "Invalid or missing route data for:",
          routeName,
        );
        return;
      }

      const path = route.result.routes[0].overview_path;

      let i = 0;
      let currentMarker = null;

      const interval = setInterval(() => {
        if (!window.google?.maps || i >= path.length - 1) {
          if (currentMarker) {
            currentMarker.setMap(null);
          }
          clearInterval(interval);
          return;
        }

        const currentPos = path[i];
        const nextPos = path[i + 1];

        const heading = window.google.maps.geometry.spherical.computeHeading(
          currentPos,
          nextPos,
        );
        const directionIcon = getDirectionalIcon(heading);

        if (currentMarker) {
          currentMarker.setMap(null);
        }

        currentMarker = new window.google.maps.Marker({
          position: currentPos,
          map,
          icon: {
            url: directionIcon,
            scaledSize: new window.google.maps.Size(40, 40),
          },
        });

        activeMarkers.push(currentMarker);
        i++;
      }, 350);

      activeIntervals.push(interval);
    });

    return () => {
      activeIntervals.forEach(clearInterval);
      activeMarkers.forEach((marker) => marker.setMap(null));
    };
  }, [selectedRoutes, routeDirections, googleMapsLoaded, map]);

  const userPlaybackRefs = useRef({});
  const invalidRouteWarningsRef = useRef(new Set());
  const [userStatus, setUserStatus] = useState({});
  const [userProgress, setUserProgress] = useState({});

  function notifyInvalidUserRoute(user, reason) {
    const userKey = String(user?.userName || user?.name || "selected user");

    if (!userKey || invalidRouteWarningsRef.current.has(userKey)) {
      return;
    }

    invalidRouteWarningsRef.current.add(userKey);
    setNotify({
      isOpen: true,
      message: `Route not created for ${userKey}`,
      type: "warning",
      pagename: componentName,
      status: reason || "No valid latitude/longitude found.",
    });
  }

  function getUserPlaybackPath(user) {
    const sortedData = [...(user?.location || [])].sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    const rawPath = sortedData
      .map((loc) => ({
        lat: parseFloat(loc.latitude),
        lng: parseFloat(loc.longitude),
        timestamp: Number(loc.timestamp) * 1000,
      }))
      .filter(
        (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
      );

    return filterCleanPoints(rawPath);
  }

  function getUserPathKey(path) {
    return path
      .map((point) => `${point.lat}:${point.lng}:${point.timestamp}`)
      .join("|");
  }

  function formatRouteTime(timestamp) {
    if (!timestamp) {
      return "--";
    }

    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  function formatPayloadDate(epochSeconds) {
    if (!epochSeconds) return "--";
    const d = new Date(epochSeconds * 1000);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  }

  function getPlaybackStatusLabel(status) {
    if (status === "paused") {
      return "Paused";
    }

    if (status === "completed") {
      return "Completed";
    }

    return "Playing";
  }

  function getPlaybackStatusStyles(status) {
    if (status === "paused") {
      return {
        color: "#FFD166",
        backgroundColor: "rgba(255, 209, 102, 0.14)",
        borderColor: "rgba(255, 209, 102, 0.26)",
      };
    }

    if (status === "completed") {
      return {
        color: "#7AE582",
        backgroundColor: "rgba(122, 229, 130, 0.14)",
        borderColor: "rgba(122, 229, 130, 0.26)",
      };
    }

    return {
      color: "#9FD4FF",
      backgroundColor: "rgba(96, 165, 250, 0.14)",
      borderColor: "rgba(96, 165, 250, 0.28)",
    };
  }

  function getPlaybackActionButtonSx(
    backgroundColor,
    hoverBackgroundColor,
    hoverShadow,
  ) {
    return {
      width: 30,
      height: 30,
      borderRadius: 1.4,
      color: "#fff",
      border: "1px solid rgba(255,255,255,0.12)",
      backgroundColor,
      "& .MuiSvgIcon-root": {
        fontSize: 18,
      },
      transition:
        "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
      "&:hover": {
        backgroundColor: hoverBackgroundColor,
        transform: "translateY(-1px)",
        boxShadow: `0 12px 24px ${hoverShadow}`,
      },
    };
  }

  function updatePlaybackStatus(userName, status) {
    setUserStatus((prev) =>
      prev[userName] === status ? prev : { ...prev, [userName]: status },
    );
  }

  function updatePlaybackProgress(userName, pathLength, segmentIndex) {
    const nextProgress =
      pathLength > 1
        ? Math.round((segmentIndex / (pathLength - 1)) * 100)
        : 100;

    setUserProgress((prev) =>
      prev[userName] === nextProgress
        ? prev
        : { ...prev, [userName]: nextProgress },
    );
  }

  function removeUserPlaybackState(userName) {
    setUserStatus((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, userName)) {
        return prev;
      }

      const next = { ...prev };
      delete next[userName];
      return next;
    });

    setUserProgress((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, userName)) {
        return prev;
      }

      const next = { ...prev };
      delete next[userName];
      return next;
    });
  }

  function getPlaybackHeading(path, segmentIndex) {
    const currentPoint = path[segmentIndex];
    const nextPoint = path[segmentIndex + 1] || currentPoint;

    if (
      !currentPoint ||
      !nextPoint ||
      !window.google?.maps?.geometry?.spherical
    ) {
      return 0;
    }

    return window.google.maps.geometry.spherical.computeHeading(
      currentPoint,
      nextPoint,
    );
  }

  function getUserRouteMarkerIcon(heading) {
    const half = USER_ROUTE_MARKER_SIZE / 2;
    return {
      url: getDirectionalIcon(heading),
      scaledSize: new window.google.maps.Size(
        USER_ROUTE_MARKER_SIZE,
        USER_ROUTE_MARKER_SIZE,
      ),
      anchor: new window.google.maps.Point(half, half),
    };
  }

  function getPlaybackSegmentDuration(currentPoint, nextPoint) {
    if (!currentPoint || !nextPoint) {
      return USER_ROUTE_MIN_SEGMENT_DURATION;
    }

    const timestampDelta = Math.max(
      0,
      Number(nextPoint.timestamp || 0) - Number(currentPoint.timestamp || 0),
    );

    if (!window.google?.maps?.geometry?.spherical) {
      return USER_ROUTE_MIN_SEGMENT_DURATION;
    }

    const distanceMeters =
      window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(currentPoint),
        new window.google.maps.LatLng(nextPoint),
      );
    const distanceDuration = Math.sqrt(Math.max(distanceMeters, 1)) * 28;
    const timestampDuration = timestampDelta ? timestampDelta / 50 : 0;

    return Math.round(
      Math.max(
        USER_ROUTE_MIN_SEGMENT_DURATION,
        Math.min(
          USER_ROUTE_MAX_SEGMENT_DURATION,
          Math.max(distanceDuration, timestampDuration),
        ),
      ),
    );
  }

  function clearUserPlaybackFrame(userName) {
    const playback = userPlaybackRefs.current[userName];

    if (playback?.frameId) {
      window.cancelAnimationFrame(playback.frameId);
      playback.frameId = null;
    }
  }

  function clearUserPlayback(userName, removeState = false) {
    clearUserPlaybackFrame(userName);

    const playback = userPlaybackRefs.current[userName];
    if (playback?.marker) {
      playback.marker.setMap(null);
    }
    delete userPlaybackRefs.current[userName];

    if (removeState) {
      removeUserPlaybackState(userName);
    }
  }

  function finishUserPlayback(userName) {
    const playback = userPlaybackRefs.current[userName];

    if (!playback) {
      return;
    }

    clearUserPlaybackFrame(userName);
    playback.status = "completed";
    playback.segmentStartTime = null;
    playback.segmentElapsedMs = 0;
    updatePlaybackStatus(userName, "completed");
    updatePlaybackProgress(
      userName,
      playback.path.length,
      playback.path.length - 1,
    );
  }

  function scheduleUserPlayback(userName) {
    const playback = userPlaybackRefs.current[userName];

    if (!playback || playback.status !== "playing") {
      return;
    }

    playback.frameId = window.requestAnimationFrame((timestamp) => {
      stepUserPlayback(userName, timestamp);
    });
  }

  function stepUserPlayback(userName, timestamp) {
    const playback = userPlaybackRefs.current[userName];

    if (!playback || playback.status !== "playing") {
      return;
    }

    const { marker, path } = playback;
    const currentPoint = path[playback.segmentIndex];
    const nextPoint = path[playback.segmentIndex + 1];

    if (!marker || !currentPoint || !nextPoint) {
      finishUserPlayback(userName);
      return;
    }

    if (playback.segmentStartTime === null) {
      playback.segmentStartTime = timestamp - playback.segmentElapsedMs;
    }

    const elapsed = timestamp - playback.segmentStartTime;
    const rawProgress = Math.min(elapsed / playback.segmentDuration, 1);
    const easedProgress = easeInOut(rawProgress);

    const lat = currentPoint.lat + (nextPoint.lat - currentPoint.lat) * easedProgress;
    const lng = currentPoint.lng + (nextPoint.lng - currentPoint.lng) * easedProgress;
    marker.setPosition({ lat, lng });

    // Interpolate heading — only call setIcon when the direction PNG bucket changes
    const interpolatedHeading = lerpAngle(playback.prevHeading, playback.currentHeading, easedProgress);
    const newDirectionUrl = getDirectionalIcon(interpolatedHeading);
    if (newDirectionUrl !== playback.lastRenderedDirection) {
      playback.lastRenderedDirection = newDirectionUrl;
      marker.setIcon(getUserRouteMarkerIcon(interpolatedHeading));
    }

    if (rawProgress >= 1) {
      marker.setPosition(nextPoint);
      playback.prevHeading = playback.currentHeading;
      playback.segmentIndex += 1;
      updatePlaybackProgress(userName, path.length, playback.segmentIndex);
      playback.segmentElapsedMs = 0;
      playback.segmentStartTime = timestamp;

      if (playback.segmentIndex >= path.length - 1) {
        finishUserPlayback(userName);
        return;
      }

      playback.segmentDuration = getPlaybackSegmentDuration(
        path[playback.segmentIndex],
        path[playback.segmentIndex + 1],
      );
      playback.currentHeading = getPlaybackHeading(path, playback.segmentIndex);
    } else {
      playback.segmentElapsedMs = elapsed;
    }

    scheduleUserPlayback(userName);
  }

  function mountUserPlayback(user, path = getUserPlaybackPath(user)) {
    if (!map || !googleMapsLoaded || !window.google?.maps?.Marker) {
      return;
    }

    if (path.length < 2) {
      notifyInvalidUserRoute(
        user,
        "Valid route requires at least two latitude/longitude points.",
      );
      clearUserPlayback(user.userName, true);
      return;
    }

    invalidRouteWarningsRef.current.delete(String(user?.userName || ""));

    clearUserPlayback(user.userName);

    const initialHeading = getPlaybackHeading(path, 0);

    const marker = new window.google.maps.Marker({
      position: path[0],
      map,
      icon: getUserRouteMarkerIcon(initialHeading),
      optimized: false,
      zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + 2,
    });

    userPlaybackRefs.current[user.userName] = {
      marker,
      path,
      pathKey: getUserPathKey(path),
      segmentIndex: 0,
      segmentElapsedMs: 0,
      segmentStartTime: null,
      segmentDuration: getPlaybackSegmentDuration(path[0], path[1]),
      frameId: null,
      status: "playing",
      prevHeading: initialHeading,
      currentHeading: initialHeading,
      lastRenderedDirection: getDirectionalIcon(initialHeading),
    };

    updatePlaybackStatus(user.userName, "playing");
    updatePlaybackProgress(user.userName, path.length, 0);
    scheduleUserPlayback(user.userName);
  }

  function focusUserRoutes(routes = userPaths) {
    if (
      !map ||
      !googleMapsLoaded ||
      !routes?.length ||
      !window.google?.maps?.LatLngBounds
    ) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let pointCount = 0;

    routes.forEach((user) => {
      getUserPlaybackPath(user).forEach((point) => {
        bounds.extend(point);
        pointCount += 1;
      });
    });

    if (!pointCount) {
      return;
    }

    if (pointCount === 1) {
      map.panTo(bounds.getCenter());
      map.setZoom(mapDefaultZoom);
      return;
    }

    map.fitBounds(bounds, USER_ROUTE_FIT_BOUNDS_PADDING);
  }

  useEffect(() => {
    if (!googleMapsLoaded || !map) return;

    const activeUsers = new Set();

    userPaths.forEach((user) => {
      const path = getUserPlaybackPath(user);

      if (path.length < 2) {
        notifyInvalidUserRoute(
          user,
          "No valid latitude/longitude found for the selected route data.",
        );
        clearUserPlayback(user.userName, true);
        return;
      }

      invalidRouteWarningsRef.current.delete(String(user?.userName || ""));
      activeUsers.add(user.userName);

      const pathKey = getUserPathKey(path);
      const existingPlayback = userPlaybackRefs.current[user.userName];

      if (
        !existingPlayback ||
        existingPlayback.pathKey !== pathKey ||
        existingPlayback.marker?.getMap?.() !== map
      ) {
        mountUserPlayback(user, path);
      }
    });

    Object.keys(userPlaybackRefs.current).forEach((userName) => {
      if (!activeUsers.has(userName)) {
        clearUserPlayback(userName, true);
      }
    });

    if (!activeUsers.size) {
      setUserStatus({});
      setUserProgress({});
      return;
    }

    focusUserRoutes(userPaths);
  }, [userPaths, googleMapsLoaded, map, mapDefaultZoom]);

  useEffect(() => {
    return () => {
      Object.keys(userPlaybackRefs.current).forEach((userName) => {
        clearUserPlayback(userName);
      });
    };
  }, []);

  const handlePausePlay = (userName) => {
    const playback = userPlaybackRefs.current[userName];

    if (!playback) {
      handleRestart(userName);
      return;
    }

    if (playback.status === "completed") {
      handleRestart(userName);
      return;
    }

    if (playback.status === "playing") {
      clearUserPlaybackFrame(userName);

      if (playback.segmentStartTime !== null) {
        playback.segmentElapsedMs =
          performance.now() - playback.segmentStartTime;
      }

      playback.segmentStartTime = null;
      playback.status = "paused";
      updatePlaybackStatus(userName, "paused");
      return;
    }

    playback.status = "playing";
    playback.segmentStartTime = null;
    updatePlaybackStatus(userName, "playing");
    scheduleUserPlayback(userName);
  };

  const handleRestart = (userName) => {
    const user = userPaths.find((item) => item.userName === userName);

    if (!user) {
      return;
    }

    mountUserPlayback(user);
  };

  const handleRemove = (userName) => {
    clearUserPlayback(userName, true);
    setUserPaths((prev) => prev.filter((user) => user.userName !== userName));
  };

  // marker create on map
  useEffect(() => {
    if (!map || !googleMapsLoaded) return;

    // Cleanup previous markers and their animations
    markersRef.current.forEach((marker) => {
      if (marker.animationIntervalId) {
        clearInterval(marker.animationIntervalId);
      }
      marker.setMap(null);
    });
    markersRef.current = [];
    // Remove any lingering mini popups from the previous marker set
    clearMiniPopup(dragMiniPopupRef);
    clearMiniPopup(hoverMiniPopupRef);
    clearMiniPopup(dropTargetPopupRef);
    dropTargetRef.current = null;

    // Registry of markers used for drag-over hit testing
    const markerRegistry = [];

    // Convert a LatLng to zoom-independent world pixels for proximity checks
    const getMarkerPixel = (latLng) => {
      const projection = map.getProjection();
      if (!projection) return null;
      const scale = Math.pow(2, map.getZoom());
      const worldPoint = projection.fromLatLngToPoint(
        latLng instanceof window.google.maps.LatLng
          ? latLng
          : new window.google.maps.LatLng(latLng.lat, latLng.lng),
      );
      return { x: worldPoint.x * scale, y: worldPoint.y * scale };
    };

    const DRAG_OVER_THRESHOLD_PX = 46;

    const newMarkers = visibleMarkers.map((item) => {
      const isEmergency = item.emergencyStatus === "Emergency";
      const isManFallDown = item.emergencyStatus === "ManFallDown";
      const marker = new window.google.maps.Marker({
        position: {
          lat: item._lat,
          lng: item._lng,
        },
        draggable: true,
        icon: isEmergency
          ? {
            path: "M -15 -15 H 15 V 15 H -15 Z M -5 -15 V -5 H -15 V 5 H -5 V 15 H 5 V 5 H 15 V -5 H 5 V -15 Z",
            fillColor: "#ff0000",
            fillOpacity: 1.5,
            strokeColor: "#ff0000",
            strokeWeight: 3,
            scale: 0.5,
            anchor: new window.google.maps.Point(0, 0),
          }
          : isManFallDown
            ? {
              path: "M 0 -16 L 14 12 H -14 Z",
              fillColor: "#FFA000",
              fillOpacity: 1,
              strokeColor: "#FF6F00",
              strokeWeight: 2,
              scale: 1.2,
              anchor: new window.google.maps.Point(0, 0),
            }
            : {
              path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
              fillColor: getColorByDepartment(item.department),
              fillOpacity: 1,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              scale: 1.8,
              anchor: new window.google.maps.Point(12, 24),
              labelOrigin: new window.google.maps.Point(12, 10),
            },
      });

      if (
        isEmergency &&
        window.google?.maps?.Size &&
        visibleMarkers.length <= 350
      ) {
        const emergencySvg = `
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <!-- Red background -->
            <rect x="2" y="2" width="28" height="28" rx="4" fill="#ff0000" stroke="none"/>
            <!-- White plus/cross in center -->
            <g fill="white">
              <rect x="14" y="5" width="4" height="22" rx="2"/>
              <rect x="5" y="14" width="22" height="4" rx="2"/>
            </g>
          </svg>
        `;

        let animationFrame = 0;
        const animationInterval = setInterval(() => {
          // Double-check that marker and google maps are still available
          if (!marker || !window.google?.maps?.Size) {
            clearInterval(animationInterval);
            return;
          }

          animationFrame = (animationFrame + 1) % 20; // 2 second animation (20 frames @ 100ms)

          // Scale oscillation: 0.8 to 1.3
          const scale =
            0.8 + Math.abs(Math.cos((animationFrame / 20) * Math.PI * 2)) * 0.5;

          marker.setIcon({
            url:
              "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(emergencySvg),
            scaledSize: new window.google.maps.Size(32 * scale, 32 * scale),
          });
        }, 100);

        // Cleanup interval on marker removal
        marker.animationIntervalId = animationInterval;
      }

      // Add ringing animation for ManFallDown markers
      if (
        isManFallDown &&
        window.google?.maps?.Size &&
        visibleMarkers.length <= 350
      ) {
        const fallDownSvg = `
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <polygon points="16,2 30,30 2,30" fill="#FFA000" stroke="#FF6F00" stroke-width="2"/>
            <text x="16" y="27" text-anchor="middle" font-size="16" font-weight="bold" fill="#000">!</text>
          </svg>
        `;

        let animationFrame = 0;
        const animationInterval = setInterval(() => {
          if (!marker || !window.google?.maps?.Size) {
            clearInterval(animationInterval);
            return;
          }

          animationFrame = (animationFrame + 1) % 20;

          const scale =
            0.8 + Math.abs(Math.cos((animationFrame / 20) * Math.PI * 2)) * 0.5;

          marker.setIcon({
            url:
              "data:image/svg+xml;charset=UTF-8," +
              encodeURIComponent(fallDownSvg),
            scaledSize: new window.google.maps.Size(32 * scale, 32 * scale),
          });
        }, 100);

        marker.animationIntervalId = animationInterval;
      }

      // Hover mini popup (success/green) — opens on hover, auto-closes on mouseout
      marker.addListener("mouseover", () => {
        clearMiniPopup(hoverMiniPopupRef);

        const position = marker.getPosition();

        // Move popup slightly above the marker
        const popupPosition = new google.maps.LatLng(
          position.lat() + 0.00003, 
          position.lng()
        );

        hoverMiniPopupRef.current = createMiniPopup(
          map,
          popupPosition,
          buildMiniPopupHTML(item, "hover")
        );
      });
      marker.addListener("mouseout", () => {
        clearMiniPopup(hoverMiniPopupRef);
      });

      marker.addListener("dragstart", () => {
        handleDragStart(item);
        // Drag mini popup (info/blue) — follows the marker while dragging
        clearMiniPopup(hoverMiniPopupRef);
        clearMiniPopup(dragMiniPopupRef);
        dragMiniPopupRef.current = createMiniPopup(
          map,
          marker.getPosition(),
          buildMiniPopupHTML(item, "drag"),
        );
      });
      marker.addListener("drag", (e) => {
        if (dragMiniPopupRef.current) {
          dragMiniPopupRef.current.updatePosition(e.latLng);
        }

        // Open the popup of the marker the dragged one is hovering over
        const dragPixel = getMarkerPixel(e.latLng);
        if (!dragPixel) return;

        let nearestEntry = null;
        let nearestDistance = Infinity;
        markerRegistry.forEach((entry) => {
          if (entry.marker === marker) return;
          const entryPixel = getMarkerPixel(entry.marker.getPosition());
          if (!entryPixel) return;
          const distance = Math.hypot(
            entryPixel.x - dragPixel.x,
            entryPixel.y - dragPixel.y,
          );
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestEntry = entry;
          }
        });

        if (nearestEntry && nearestDistance <= DRAG_OVER_THRESHOLD_PX) {
          if (dropTargetRef.current !== nearestEntry.marker) {
            clearMiniPopup(dropTargetPopupRef);
            dropTargetPopupRef.current = createMiniPopup(
              map,
              nearestEntry.marker.getPosition(),
              buildMiniPopupHTML(nearestEntry.item, "hover"),
            );
            dropTargetRef.current = nearestEntry.marker;
          }
        } else if (dropTargetRef.current) {
          clearMiniPopup(dropTargetPopupRef);
          dropTargetRef.current = null;
        }
      });
      marker.addListener("dragend", (e) => {
        clearMiniPopup(dragMiniPopupRef);
        clearMiniPopup(dropTargetPopupRef);
        dropTargetRef.current = null;
        const droppedPos = e.latLng.toJSON();
        handleDragEnd({
          ...item,
          position: droppedPos,
          userCoordinateId: item.userCoordinateId, // make sure this is included!
        });
      });
      marker.addListener("click", async () => {
        const lat = item._lat;
        const lng = item._lng;
        const address = await getAddressFromLatLng(lat, lng);

        const clicked = {
          ...item,
          position: { lat, lng },
          address,
          hover: false,
        };

        setSelectedMarker((prev) => {
          if (prev && prev.userCoordinateId === clicked.userCoordinateId) {
            return null;
          }
          return clicked;
        });
        // Filter and sort nearby markers by distance
        const sortedNearby = normalizedMarkers
          .filter((m) => m.userCoordinateId !== item.userCoordinateId)
          .map((m) => {
            const distance =
              window.google.maps.geometry.spherical.computeDistanceBetween(
                new window.google.maps.LatLng(clicked.position),
                new window.google.maps.LatLng(m._lat, m._lng),
              );
            return { ...m, distance };
          })
          .sort((a, b) => a.distance - b.distance);

        setSortedNearbyMarkers(sortedNearby);
      });

      markersRef.current.push(marker);
      markerRegistry.push({ marker, item });
      return marker;
    });

    // Destroy previous clusterer if it exists
    if (clusterRef.current) {
      clusterRef.current.clearMarkers();
    }

    // clusterRef.current = new MarkerClusterer({ markers: newMarkers, map });
    clusterRef.current = new MarkerClusterer({
      markers: newMarkers,
      map,
      renderer: {
        render: ({ count, position, markers }) => {
          const colors = [
            ...new Set(
              markers.map((m) => {
                const icon = m.getIcon();
                return typeof icon === "object" && icon?.fillColor
                  ? icon.fillColor
                  : "#9999ff";
              }),
            ),
          ];

          const size = 50;
          const radius = 20;
          const cx = size / 2;
          const cy = size / 2;

          let svgContent = "";

          if (colors.length === 1) {
            svgContent = `
              <circle cx="${cx}" cy="${cy}" r="${radius}" fill="${colors[0]}" />
            `;
          } else {
            const sliceAngle = (2 * Math.PI) / colors.length;
            for (let i = 0; i < colors.length; i++) {
              const startAngle = i * sliceAngle;
              const endAngle = startAngle + sliceAngle;
              const x1 = cx + radius * Math.cos(startAngle);
              const y1 = cy + radius * Math.sin(startAngle);
              const x2 = cx + radius * Math.cos(endAngle);
              const y2 = cy + radius * Math.sin(endAngle);
              const largeArcFlag = sliceAngle > Math.PI ? 1 : 0;

              svgContent += `
                <path d="M${cx},${cy} L${x1},${y1} A${radius},${radius} 0 ${largeArcFlag},1 ${x2},${y2} Z"
                      fill="${colors[i]}" />
              `;
            }
          }

          const svg = `
          <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="clusterShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#000" flood-opacity="0.3" />
              </filter>
            </defs>
            
            <g filter="url(#clusterShadow)">
              ${svgContent}
              <!-- Inner white border -->
              <circle cx="${cx}" cy="${cy}" r="${radius}" fill="none" stroke="#fff" stroke-width="2" />
              <!-- Outer semi-transparent border -->
              <circle cx="${cx}" cy="${cy}" r="${radius + 2
            }" fill="none" stroke="#000" stroke-opacity="0.2" stroke-width="3" />
            </g>
            
            <text x="${cx}" y="${cy + 4
            }" text-anchor="middle" fill="#fff" font-size="14" font-weight="bold">${count}</text>
          </svg>`;

          return new window.google.maps.Marker({
            position,
            icon: {
              url:
                "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
              scaledSize: new window.google.maps.Size(size, size),
            },
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      },
    });
  }, [map, googleMapsLoaded, visibleMarkers, normalizedMarkers]);

  const handleLoad = (map) => {
    setMapRef(map);
    setMap(map);
    setGoogleMapsLoaded(true);
    setMapTypeId(map.getMapTypeId?.() || "satellite");
    const currentCenter = map.getCenter();
    setMapCenter(currentCenter.toJSON());
    setZoomLevel(map.getZoom());

    if (!autocompleteService && !googleMapsLoaded) {
      setAutocompleteService(
        new window.google.maps.places.AutocompleteService(),
      );
      setPlacesService(new window.google.maps.places.PlacesService(map));
    }

    // Enforce compact map-type control and top-left position after map init.
    map.setOptions({
      // controlSize: 34,
      mapTypeControl: false,
      styles: mapLabelsEnabled ? mapStyles : mapStylesWithoutLabels,
      zoomControlOptions: {
        position: window.google?.maps?.ControlPosition?.LEFT_TOP,
      },
      streetViewControlOptions: {
        position: window.google?.maps?.ControlPosition?.LEFT_TOP,
      },
      rotateControlOptions: {
        position: window.google?.maps?.ControlPosition?.LEFT_TOP,
      },
      fullscreenControlOptions: {
        position: window.google?.maps?.ControlPosition?.LEFT_TOP,
      },
      zoomControl: false,
    });

    // Controls are rendered async by Google Maps; apply in small retries.
    window.setTimeout(() => applyTinyMapControls(map), 40);
    window.setTimeout(() => applyTinyMapControls(map), 220);
    window.setTimeout(() => applyTinyMapControls(map), 650);
  };

  const getShapeCoords = (shape) => {
    if (shape.type === "rectangle") {
      return shape.getBounds().toJSON();
    }
    return null;
  };

  const toggleDrawing = () => {
    if (!drawingMode) {
      setDrawingMode(true);
      setIsEditing(false);
      map?.setOptions({ draggableCursor: "crosshair" });
    } else {
      setDrawingMode(false);
      map?.setOptions({ draggableCursor: null, draggable: true });
    }
  };

  const editShape = () => {
    if (selectedShape) {
      selectedShape.setEditable(true);
      setIsEditing(true);
    }
  };

  const saveShape = () => {
    if (selectedShape) {
      selectedShape.setEditable(false);
      const updatedCoords = getShapeCoords(selectedShape);
      loggerFactory.info(
        componentName,
        "Updated shape bounds/coords:",
        JSON.stringify(updatedCoords),
      );
      onAreaCreated(updatedCoords);
      setIsEditing(false);
    }
  };

  const deleteShape = () => {
    if (selectedShape) {
      selectedShape.setMap(null);
      setSelectedShape(null);
      selectedShapeRef.current = null;
      currentRectRef.current = null;
      setIsEditing(false);
    }
  };

  const MyLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const accuracyThreshold = 100;
        if (accuracy > accuracyThreshold) {
          setNotify({
            isOpen: true,
            message:
              "For better accuracy, move closer to a window or ensure location services are enabled.",
            type: "warning",
            pagename: componentName,
            status: "Your location was retrieved but appears to be imprecise. ",
          });
        }
        setMapCenter({ lat: latitude, lng: longitude });
        setCurrentLocation({ lat: latitude, lng: longitude });
        mapRef?.panTo({ lat: latitude, lng: longitude });
        mapRef?.setZoom(16);
      },
      (error) => {
        let notification = { type: "error", status: "", message: "" };

        switch (error.code) {
          case error.PERMISSION_DENIED:
            notification.status = "Location access was denied. ";
            notification.message =
              "Please enable permissions in your browser or device settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            notification.status =
              "Location information is currently unavailable.";
            notification.message = "Please check your network or GPS signal.";
            break;
          case error.TIMEOUT:
            notification.status = "Location request timed out.";
            notification.message = "Try again or check your connection.";
            break;
          default:
            notification.status =
              "An unexpected error occurred while retrieving your location.";
            break;
        }

        setNotify({
          isOpen: true,
          message: notification.message,
          type: notification.type,
          pagename: componentName,
          status: notification.status,
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  useEffect(() => {
    MyLocation();
  }, []);

  const handleDragStart = (item) => {
    dragStartRef.current = item;
    originalPositionsRef.current[item.userCoordinateId] = {
      lat: parseFloat(item.geoX.trim()),
      lng: parseFloat(item.geoY.trim()),
    };
  };

  const handleDragEnd = (item) => {
    if (!mapRef || !item.position) return;

    const dragStart = dragStartRef.current;
    if (!dragStart) {
      loggerFactory.info(
        componentName,
        "drag Start is null.",
        "Aborting dragEnd logic.",
      );
      return;
    }

    const dragEndPosition = item.position;
    const candidates = filteredList
      .filter(
        (i) =>
          i.userCoordinateId !== item.userCoordinateId &&
          Number.isFinite(i._lat) && Number.isFinite(i._lng),
      )
      .map((i) => {
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(dragEndPosition),
            new window.google.maps.LatLng(i._lat, i._lng),
          );
        return { ...i, distance };
      });

    if (candidates.length === 0) {
      loggerFactory.info(componentName, "No other markers found.");
    } else {
      const nearest = candidates.sort((a, b) => a.distance - b.distance)[0];

      dragToCall(dragStart, nearest);
      loggerFactory.info(componentName, "Dragged Marker:", dragStart);
      loggerFactory.info(componentName, "Nearest Marker:", nearest);
    }
    const updated = filteredList.map((m) =>
      m.userCoordinateId === item.userCoordinateId
        ? {
          ...m,
          position: {
            lat: Number.parseFloat(String(dragStart.geoX || "").trim()),
            lng: Number.parseFloat(String(dragStart.geoY || "").trim()),
          },
        }
        : m,
    );
    setMarkerData(updated);
  };

  const fetchPredictions = (input) => {
    if (!autocompleteService || !input) {
      setPredictions([]);
      return;
    }

    autocompleteService.getPlacePredictions({ input }, (results, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        results
      ) {
        setPredictions(results);
      } else {
        setPredictions([]);
      }
    });
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setDestination(val);
    fetchPredictions(val);
  };

  const handleSelectPrediction = (place) => {
    setDestination(place.description);
    setPredictions([]);

    if (!placesService) return;

    placesService.getDetails(
      { placeId: place.place_id },
      (placeResult, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          placeResult.geometry
        ) {
          const loc = {
            lat: placeResult.geometry.location.lat(),
            lng: placeResult.geometry.location.lng(),
          };
          setDirections({
            origin: currentLocation,
            destination: loc,
            travelMode: "DRIVING",
          });
          mapRef?.panTo(loc);
        }
      },
    );
  };

  const filterCleanPoints = (path) => {
    if (!Array.isArray(path) || path.length < 2) {
      return Array.isArray(path) ? path : [];
    }

    const sortedPath = [...path].sort(
      (a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0),
    );

    const deduped = [];

    sortedPath.forEach((point, pointIndex) => {
      if (!deduped.length) {
        deduped.push(point);
        return;
      }

      const previous = deduped[deduped.length - 1];
      const distance = getDistanceMeters(previous, point);
      const timeDeltaSeconds = Math.max(
        0,
        (Number(point.timestamp || 0) - Number(previous.timestamp || 0)) / 1000,
      );

      if (
        distance <= USER_ROUTE_DUPLICATE_DISTANCE_METERS &&
        timeDeltaSeconds <= 8
      ) {
        // Keep the newest sample for stationary jitter clusters.
        deduped[deduped.length - 1] = point;
        return;
      }

      if (timeDeltaSeconds > 0) {
        const speedMps = distance / timeDeltaSeconds;
        if (speedMps > USER_ROUTE_MAX_SPEED_MPS && timeDeltaSeconds <= 3) {
          return;
        }
      }

      if (
        distance > USER_ROUTE_SUDDEN_JUMP_METERS &&
        (timeDeltaSeconds === 0 ||
          timeDeltaSeconds <= USER_ROUTE_SUDDEN_JUMP_MAX_SECONDS)
      ) {
        const hasQuickReturn = sortedPath
          .slice(
            pointIndex + 1,
            pointIndex + 1 + USER_ROUTE_SUDDEN_JUMP_RETURN_WINDOW,
          )
          .some(
            (candidate) =>
              getDistanceMeters(previous, candidate) <=
              USER_ROUTE_SUDDEN_JUMP_RETURN_METERS,
          );

        if (hasQuickReturn) {
          return;
        }
      }

      deduped.push(point);
    });

    if (deduped.length < 3) {
      return deduped;
    }

    const cleaned = [deduped[0]];

    for (let index = 1; index < deduped.length - 1; index += 1) {
      const prev = cleaned[cleaned.length - 1];
      const current = deduped[index];
      const next = deduped[index + 1];

      const prevToCurrent = getDistanceMeters(prev, current);
      const currentToNext = getDistanceMeters(current, next);
      const prevToNext = getDistanceMeters(prev, next);

      const inSeconds = Math.max(
        0,
        (Number(current.timestamp || 0) - Number(prev.timestamp || 0)) / 1000,
      );
      const outSeconds = Math.max(
        0,
        (Number(next.timestamp || 0) - Number(current.timestamp || 0)) / 1000,
      );

      const speedIn = inSeconds > 0 ? prevToCurrent / inSeconds : 0;
      const speedOut = outSeconds > 0 ? currentToNext / outSeconds : 0;

      const isReturnSpike =
        prevToCurrent > USER_ROUTE_SPIKE_JUMP_METERS &&
        currentToNext > USER_ROUTE_SPIKE_JUMP_METERS &&
        prevToNext < USER_ROUTE_SPIKE_RETURN_METERS;

      const hasQuickReturn = deduped
        .slice(index + 1, index + 1 + USER_ROUTE_SUDDEN_JUMP_RETURN_WINDOW)
        .some(
          (candidate) =>
            getDistanceMeters(prev, candidate) <=
            USER_ROUTE_SUDDEN_JUMP_RETURN_METERS,
        );

      const isSuddenFarOutlier =
        prevToCurrent > USER_ROUTE_SUDDEN_JUMP_METERS &&
        (inSeconds === 0 || inSeconds <= USER_ROUTE_SUDDEN_JUMP_MAX_SECONDS) &&
        hasQuickReturn;

      const isImpossibleSegment =
        (inSeconds > 0 && speedIn > USER_ROUTE_MAX_SPEED_MPS) ||
        (outSeconds > 0 && speedOut > USER_ROUTE_MAX_SPEED_MPS);

      if (isReturnSpike || isSuddenFarOutlier || isImpossibleSegment) {
        continue;
      }

      if (
        prevToCurrent <= USER_ROUTE_TINY_MOVE_DISTANCE_METERS &&
        inSeconds <= 2
      ) {
        cleaned[cleaned.length - 1] = current;
        continue;
      }

      cleaned.push(current);
    }

    cleaned.push(deduped[deduped.length - 1]);
    return cleaned;
  };

  const handleZoomChanged = () => {
    if (mapRef) {
      setZoomLevel(mapRef.getZoom());
      const center = mapRef.getCenter();
      if (center) {
        setMapCenter(center.toJSON());
      }
    }
  };

  const handleSelectUser = async (user) => {
    const payload = {
      extension: user.user.extensionNumber,
      fromDate: user.from,
      toDate: user.to,
    };

    try {
      const res = await telemetry.post(`routeCoordinate/user`, payload);
      setOpenUserRoute(false);
      setSelectedMarker(false);
      setUserPaths(
        (res.data.data || []).map((item) => ({
          ...item,
          _fromDate: user.from,
          _toDate: user.to,
        })),
      );
      setNotify({
        isOpen: true,
        message: res?.data?.messageDetails || "User paths fetched successfully",
        type: "success",
        pagename: componentName,
        status: res?.data?.message,
      });
    } catch (error) {
      setOpenUserRoute(false);
      setSelectedMarker(false);
      setUserPaths([]);
      loggerFactory.error(`Error fetching user paths: ${error.message}`, {
        error,
      });
      setNotify({
        isOpen: true,
        status:
          error?.response?.data?.messageDetails || "Failed to fetch user paths",
        type: "error",
        pagename: componentName,
        message: error?.response?.data?.message,
      });
    }
  };

  const activeUserRoutes = useMemo(
    () =>
      userPaths
        .map((user) => {
          const path = getUserPlaybackPath(user);
          if (path.length < 2) return null;
          return { user, path, start: path[0], end: path[path.length - 1] };
        })
        .filter(Boolean),
    [userPaths],
  );

  const isSatelliteMode = mapTypeId === "satellite" || mapTypeId === "hybrid";

  const mapOptions = useMemo(
    () => ({
      mapTypeId,
      styles: mapLabelsEnabled ? mapStyles : mapStylesWithoutLabels,
      mapTypeControl: false,
      zoomControl: false,
    }),
    [mapTypeId, mapLabelsEnabled],
  );
  const secondaryControlBg = isSatelliteMode
    ? "rgba(24, 27, 32, 0.84)"
    : "rgba(255, 255, 255, 0.92)";
  const secondaryControlBorder = isSatelliteMode
    ? "1px solid rgba(255, 255, 255, 0.22)"
    : "1px solid rgba(18, 66, 133, 0.24)";
  const secondaryControlColor = isSatelliteMode
    ? "rgba(255,255,255,0.94)"
    : "rgba(23,31,43,0.92)";

  const zoomInMap = () => {
    if (!mapRef) {
      return;
    }
    const currentZoom = Number(mapRef.getZoom?.() || zoomLevel || 0);
    const nextZoom = Math.min(22, currentZoom + 1);
    mapRef.setZoom?.(nextZoom);
    setZoomLevel(nextZoom);
  };

  const zoomOutMap = () => {
    if (!mapRef) {
      return;
    }
    const currentZoom = Number(mapRef.getZoom?.() || zoomLevel || 0);
    const nextZoom = Math.max(2, currentZoom - 1);
    mapRef.setZoom?.(nextZoom);
    setZoomLevel(nextZoom);
  };

  const toggleMapMode = () => {
    if (!mapRef) {
      return;
    }
    const currentType = mapRef.getMapTypeId?.() || mapTypeId;
    const nextType =
      currentType === "satellite" || currentType === "hybrid"
        ? "roadmap"
        : mapLabelsEnabled
          ? "hybrid"
          : "satellite";
    mapRef.setMapTypeId?.(nextType);
    setMapTypeId(nextType);
    applyTinyMapControls(mapRef);
  };

  const toggleMapLabels = () => {
    if (!mapRef) {
      return;
    }

    const currentType = mapRef.getMapTypeId?.() || mapTypeId;

    if (currentType === "satellite" || currentType === "hybrid") {
      const nextType = currentType === "hybrid" ? "satellite" : "hybrid";
      const nextLabelsEnabled = nextType === "hybrid";
      mapRef.setMapTypeId?.(nextType);
      setMapTypeId(nextType);
      setMapLabelsEnabled(nextLabelsEnabled);
      applyTinyMapControls(mapRef);
      return;
    }

    setMapLabelsEnabled((previous) => {
      const nextValue = !previous;
      mapRef.setOptions?.({
        styles: nextValue ? mapStyles : mapStylesWithoutLabels,
      });
      return nextValue;
    });
  };

  return (
    <LoadScript
      googleMapsApiKey={"AIzaSyAZVqRpoEL3WRH4f0Tsj4BSwNMv781-GYM"}
      libraries={libraries}
    >
      {/* Google map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={zoomLevel}
        onZoomChanged={handleZoomChanged}
        onDragEnd={handleZoomChanged}
        onClick={(e) =>
          loggerFactory.info(
            componentName,
            "Clicked coordinates:",
            e.latLng.toJSON(),
          )
        }

        options={mapOptions}
        onLoad={handleLoad}
        onMapTypeIdChanged={() => {
          if (mapRef?.getMapTypeId) {
            const currentType = mapRef.getMapTypeId();
            setMapTypeId(currentType);
            if (currentType === "satellite") {
              setMapLabelsEnabled(false);
            } else if (currentType === "hybrid") {
              setMapLabelsEnabled(true);
            }
            applyTinyMapControls(mapRef);
          }
        }}
      >
        <Box
          data-geofence-control="true"
          sx={{
            position: "absolute",
            left: 1,
            top: 120,
            zIndex: 13,
          }}
        >
          <Card
            sx={{
              borderRadius: 15,
              p: 0.35,
              display: "grid",
              rowGap: 0.6,
              background: secondaryControlBg,
              border: secondaryControlBorder,
              boxShadow: isSatelliteMode
                ? "0 6px 14px rgba(0, 0, 0, 0.35)"
                : "0 6px 14px rgba(15, 23, 42, 0.16)",
              backdropFilter: "blur(6px)",
            }}
          >
            <Tooltip title="Zoom In" placement="right" arrow>
              <IconButton
                onClick={zoomInMap}
                sx={{
                  width: 34,
                  height: 34,
                  border: secondaryControlBorder,
                  color: secondaryControlColor,
                  bgcolor: "transparent",
                  borderRadius: "50%",
                  p: 0.4,
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Zoom Out" placement="right" arrow>
              <IconButton
                onClick={zoomOutMap}
                sx={{
                  width: 34,
                  height: 34,
                  border: secondaryControlBorder,
                  color: secondaryControlColor,
                  bgcolor: "transparent",
                  borderRadius: "50%",
                  p: 0.4,
                }}
              >
                <RemoveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Toggle Map Mode" placement="right" arrow>
              <IconButton
                onClick={toggleMapMode}
                sx={{
                  width: 34,
                  height: 34,
                  border: secondaryControlBorder,
                  color: secondaryControlColor,
                  bgcolor: "transparent",
                  borderRadius: "50%",
                  p: 0.4,
                }}
              >
                {isSatelliteMode ? (
                  <MapIcon sx={{ fontSize: 18 }} />
                ) : (
                  <SatelliteAltIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip
              title={mapLabelsEnabled ? "Disable Map Labels" : "Enable Map Labels"}
              placement="right"
              arrow
            >
              <IconButton
                onClick={toggleMapLabels}
                sx={{
                  width: 34,
                  height: 34,
                  border: secondaryControlBorder,
                  color: secondaryControlColor,
                  bgcolor: "transparent",
                  borderRadius: "50%",
                  p: 0.4,
                }}
              >
                {mapLabelsEnabled ? (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Create Geofence" placement="right" arrow>
              <IconButton
                onClick={(e) => setGeofenceMenuAnchor(e.currentTarget)}
                sx={{
                  width: 34,
                  height: 34,
                  border: secondaryControlBorder,
                  color: geofenceDrawShapeType ? "#22c55e" : secondaryControlColor,
                  bgcolor: "transparent",
                  borderRadius: "50%",
                  p: 0.4,
                }}
              >
                <PentagonIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Card>

          <Menu
            anchorEl={geofenceMenuAnchor}
            open={Boolean(geofenceMenuAnchor)}
            onClose={() => setGeofenceMenuAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                setGeofenceMenuAnchor(null);
                startGeofenceDraw("polygon");
              }}
            >
              Polygon
            </MenuItem>
            <MenuItem
              onClick={() => {
                setGeofenceMenuAnchor(null);
                startGeofenceDraw("circle");
              }}
            >
              Circle
            </MenuItem>
            <MenuItem
              onClick={() => {
                setGeofenceMenuAnchor(null);
                startGeofenceDraw("rectangle");
              }}
            >
              Rectangle
            </MenuItem>
          </Menu>
        </Box>

        {geofenceDrawShapeType && (
          <Box
            data-geofence-control="true"
            sx={{
              position: "absolute",
              top: 12,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 20,
            }}
          >
            <Card
              sx={{
                px: 1.5,
                py: 0.75,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                borderRadius: 3,
                background: secondaryControlBg,
                border: secondaryControlBorder,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: secondaryControlColor, fontWeight: 600 }}
              >
                {geofenceDrawShapeType === "polygon"
                  ? `Tap the map to add points (${geofenceDrawPoints.length}). Add at least 3, then Finish.`
                  : geofenceDrawShapeType === "circle"
                    ? `Tap the center, then the edge (${geofenceDrawPoints.length}/2), then Finish.`
                    : `Tap two opposite corners (${geofenceDrawPoints.length}/2), then Finish.`}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Tooltip title="Undo last point" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={undoGeofenceDrawPoint}
                      disabled={geofenceDrawPoints.length === 0}
                      sx={{ color: secondaryControlColor }}
                    >
                      <UndoIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Finish" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={finishGeofenceDraw}
                      disabled={
                        geofenceDrawShapeType === "polygon"
                          ? geofenceDrawPoints.length < 3
                          : geofenceDrawPoints.length < 2
                      }
                      sx={{ color: "#22c55e" }}
                    >
                      <CheckIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Cancel" arrow>
                  <IconButton
                    size="small"
                    onClick={cancelGeofenceDraw}
                    sx={{ color: "#ef4444" }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Box>
        )}

        {overlayActionPanel && (
          <Box
            sx={{
              position: "absolute",
              right: 10,
              top: 12,
              zIndex: 14,
              pointerEvents: "none",
              "& > *": {
                pointerEvents: "auto",
              },
            }}
          >
            {React.isValidElement(overlayActionPanel)
              ? React.cloneElement(overlayActionPanel, {
                isSatelliteMode:
                  mapTypeId === "satellite" || mapTypeId === "hybrid",
              })
              : overlayActionPanel}
          </Box>
        )}

        <MAPActionButton
          data={data}
          setShowSearch={setShowSearch}
          mapTypeId={mapTypeId}
          isDisable={isDisable}
          locationUpdateColor={locationUpdateColor}
          onRecordingPage={onRecordingPage}
          onPage={onPage}
          onDial={onDial}
          onGroupCall={onGroupCall}
          onLiveGroupCall={onLiveGroupCall}
          onMessage={onMessage}
          onEmergency={onEmergency}
          onNavigateHome={onNavigateHome}
          onAnchorLocation={onAnchorLocation}
          MyLocation={MyLocation}
          drawingMode={drawingMode}
          toggleDrawing={toggleDrawing}
          selectedShape={selectedShape}
          isEditing={isEditing}
          editShape={editShape}
          saveShape={saveShape}
          deleteShape={deleteShape}
          showArea={showArea}
          setShowArea={setShowArea}
          showRoute={showRoute}
          setShowRoute={setShowRoute}
          setShowRouteList={setShowRouteList}
          setUserPaths={setUserPaths}
        />

        {geofenceDrawShapeType === "polygon" && geofenceDrawPoints.length > 0 && (
          <>
            <Polyline
              path={geofenceDrawPoints}
              options={{ strokeColor: "#3388ff", strokeWeight: 2, clickable: false }}
            />
            {geofenceDrawPoints.map((point, index) => (
              <Marker
                key={`geofence-draw-point-${index}`}
                position={point}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 5,
                  fillColor: "#3388ff",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 1.5,
                }}
                clickable={false}
              />
            ))}
            {geofenceDrawPoints.length > 2 && (
              <Polygon
                path={geofenceDrawPoints}
                options={{
                  strokeColor: "#3388ff",
                  fillColor: "#3388ff",
                  fillOpacity: 0.15,
                  clickable: false,
                }}
              />
            )}
          </>
        )}

        {/* Live circle preview while drawing - shape stays until Finish */}
        {geofenceDrawShapeType === "circle" && geofenceDrawPoints.length > 0 && (
          <>
            {geofenceDrawPoints.map((point, index) => (
              <Marker
                key={`geofence-circle-point-${index}`}
                position={point}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 5,
                  fillColor: "#3388ff",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 1.5,
                }}
                clickable={false}
              />
            ))}
            {geofenceDrawPoints.length >= 2 && (
              <Circle
                center={geofenceDrawPoints[0]}
                radius={window.google.maps.geometry.spherical.computeDistanceBetween(
                  new window.google.maps.LatLng(
                    geofenceDrawPoints[0].lat,
                    geofenceDrawPoints[0].lng,
                  ),
                  new window.google.maps.LatLng(
                    geofenceDrawPoints[1].lat,
                    geofenceDrawPoints[1].lng,
                  ),
                )}
                options={{
                  strokeColor: "#3388ff",
                  fillColor: "#3388ff",
                  fillOpacity: 0.15,
                  clickable: false,
                }}
              />
            )}
          </>
        )}

        {/* Live rectangle preview while drawing - shape stays until Finish */}
        {geofenceDrawShapeType === "rectangle" &&
          geofenceDrawPoints.length > 0 && (
            <>
              {geofenceDrawPoints.map((point, index) => (
                <Marker
                  key={`geofence-rect-point-${index}`}
                  position={point}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: "#3388ff",
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 1.5,
                  }}
                  clickable={false}
                />
              ))}
              {geofenceDrawPoints.length >= 2 && (
                <Polygon
                  path={[
                    {
                      lat: Math.max(geofenceDrawPoints[0].lat, geofenceDrawPoints[1].lat),
                      lng: Math.max(geofenceDrawPoints[0].lng, geofenceDrawPoints[1].lng),
                    },
                    {
                      lat: Math.min(geofenceDrawPoints[0].lat, geofenceDrawPoints[1].lat),
                      lng: Math.max(geofenceDrawPoints[0].lng, geofenceDrawPoints[1].lng),
                    },
                    {
                      lat: Math.min(geofenceDrawPoints[0].lat, geofenceDrawPoints[1].lat),
                      lng: Math.min(geofenceDrawPoints[0].lng, geofenceDrawPoints[1].lng),
                    },
                    {
                      lat: Math.max(geofenceDrawPoints[0].lat, geofenceDrawPoints[1].lat),
                      lng: Math.min(geofenceDrawPoints[0].lng, geofenceDrawPoints[1].lng),
                    },
                  ]}
                  options={{
                    strokeColor: "#3388ff",
                    fillColor: "#3388ff",
                    fillOpacity: 0.15,
                    clickable: false,
                  }}
                />
              )}
            </>
          )}

        {/* Not-yet-saved fence, kept on the map while the editor asks for
            name + color (so circle/rectangle are visible before naming) */}
        {googleMapsLoaded &&
          geofenceEditorOpen &&
          !editingGeofence &&
          pendingGeofenceShape &&
          (pendingGeofenceShape.shapeType === "CIRCLE" ? (
            <Circle
              center={{
                lat: Number(pendingGeofenceShape.centerLat),
                lng: Number(pendingGeofenceShape.centerLng),
              }}
              radius={Number(pendingGeofenceShape.radiusMeters) || 0}
              options={{
                strokeColor: pendingPreviewColor,
                fillColor: pendingPreviewColor,
                fillOpacity: 0.3,
                strokeWeight: 2,
                clickable: false,
                zIndex: 9999,
              }}
            />
          ) : pendingGeofenceShape.coordinates?.length ? (
            <Polygon
              paths={pendingGeofenceShape.coordinates.map((point) => ({
                lat: Number(point.lat),
                lng: Number(point.lng),
              }))}
              options={{
                strokeColor: pendingPreviewColor,
                fillColor: pendingPreviewColor,
                fillOpacity: 0.3,
                strokeWeight: 2,
                clickable: false,
                zIndex: 9999,
              }}
            />
          ) : null)}

        {googleMapsLoaded &&
          (geofenceListData || []).map((geofence) => {
            const color = geofence.color || "#3388ff";
            let shapeElement = null;
            let labelPosition = null;

            if (geofence.shapeType === "CIRCLE" && geofence.centerLat && geofence.centerLng) {
              labelPosition = {
                lat: Number(geofence.centerLat),
                lng: Number(geofence.centerLng),
              };
              shapeElement = (
                <Circle
                  center={labelPosition}
                  radius={geofence.radiusMeters || 0}
                  options={{
                    strokeColor: color,
                    fillColor: color,
                    fillOpacity: 0.25,
                    clickable: false,
                  }}
                />
              );
            } else if (geofence.coordinates?.length) {
              const points = geofence.coordinates.map((point) => ({
                lat: Number(point.lat),
                lng: Number(point.lng),
              }));
              shapeElement = (
                <Polygon
                  paths={points}
                  options={{
                    strokeColor: color,
                    fillColor: color,
                    fillOpacity: 0.25,
                    clickable: false,
                  }}
                />
              );
              const total = points.reduce(
                (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
                { lat: 0, lng: 0 },
              );
              labelPosition = {
                lat: total.lat / points.length,
                lng: total.lng / points.length,
              };
            }

            if (!shapeElement) return null;

            return (
              <React.Fragment key={geofence.id}>
                {shapeElement}
                {labelPosition && geofence.name && (
                  <OverlayView
                    position={labelPosition}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                      x: -(width / 2),
                      y: -(height / 2),
                    })}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        background: "transparent",
                        color: "#ffffff",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        textShadow:
                          "0 1px 3px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.95)",
                      }}
                    >
                      {geofence.name}
                    </div>
                  </OverlayView>
                )}
              </React.Fragment>
            );
          })}

        {/* <SearchBar
          showSearch={showSearch}
          destination={destination}
          setDestination={setDestination}
          predictions={predictions}
          handleInputChange={handleInputChange}
          handleSelectPrediction={handleSelectPrediction}
          clearSearch={() => {
            setDestination("");
            setPredictions([]);
            setDirections(null);
          }}
        /> */}
        {/* After Click Marker Popup */}
        {selectedMarker && !openUserRoute && (
          <MarkerPopup
            userPaths={userPaths}
            selectedMarker={selectedMarker}
            setSelectedMarker={setSelectedMarker}
            clickToMessageMarker={clickToMessageMarker}
            dragCallTransfer={dragCallTransfer}
            handleClearEmergency={handleClearEmergency}
            handleLiveMonitoring={handleLiveMonitoring}
            setOpenUserRoute={setOpenUserRoute}
            setNotify={setNotify}
            onContactAction={onContactAction}
          />
        )}

        {openUserRoute && (
          <UserRoute
            open={openUserRoute}
            onClose={() => setOpenUserRoute(false)}
            users={selectedMarker}
            handleSelectUser={handleSelectUser}
          />
        )}

        {isMonitoring && liveUserRef.current && (
          <MonitoringPill
            name={
              liveUserRef.current?.designation ||
              liveUserRef.current?.extension ||
              "Unknown"
            }
            loading={loading}
            onStop={handleClearMonitoring}
          />
        )}

        {/* After Search Location Direction Route */}
        {directions &&
          directions.origin &&
          directions.destination &&
          !directions.routes && (
            <DirectionsService
              options={directions}
              callback={(res) => {
                if (res.status === "OK") {
                  setDirections({
                    ...res,
                    info: {
                      from: res.routes[0].legs[0].start_address,
                      to: res.routes[0].legs[0].end_address,
                      distance: res.routes[0].legs[0].distance.text,
                      duration: res.routes[0].legs[0].duration.text,
                    },
                  });
                }
              }}
            />
          )}

        {directions && directions.routes && (
          <>
            <DirectionsRenderer directions={directions} />
            <Marker
              position={directions.routes[0].legs[0].start_location}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: "#1976D2",
                fillOpacity: 1,
                strokeWeight: 2,
                strokeColor: "#ffffff",
              }}
            />
          </>
        )}

        {/* Direction Info time distance */}
        {directions?.info && (
          <DirectionsInfoBox
            directions={directions}
            nodeRef={nodeRef}
            showSearch={showSearch}
          />
        )}
        {/* Pre define coordinate to make route  */}
        {showRoute && showRouteList && (
          <Box
            sx={{
              position: "absolute",
              top: 250,
              right: 55,
              zIndex: 10,
              backgroundColor: "#ffffff",
              color: "#000000",
              borderRadius: 2,
              boxShadow: 4,
              width: 250,
            }}
          >
            {/* Header with Title and Close Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1,
                borderBottom: "1px solid #ccc",
                backgroundColor: "#ffffff",
                position: "sticky",
                borderRadius: 2,
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: "#000000", fontWeight: 600 }}
              >
                Select Routes to Display
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowRouteList(false)}
                sx={{ color: "#000" }}
              >
                <CloseIcon fontSize="small" color="error" />
              </IconButton>
            </Box>

            {/* Scrollable List */}
            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              <List dense disablePadding>
                {Object.values(routeDirections).map((route, idx) => {
                  const isChecked = selectedRoutes.includes(route.name);
                  const routeKey = route.id || route.name || idx;

                  return (
                    <React.Fragment key={routeKey}>
                      {idx > 0 && (
                        <Divider
                          sx={{
                            my: 1,
                            height: "1px",
                            width: "100%",
                            backgroundColor: "#ccc",
                          }}
                        />
                      )}
                      <ListItem
                        disableGutters
                        dense
                        sx={{
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? [...selectedRoutes, route.name]
                                : selectedRoutes.filter(
                                  (name) => name !== route.name,
                                );
                              setSelectedRoutes(updated);
                            }}
                            icon={
                              <CheckBoxOutlineBlankIcon
                                sx={{ color: "#000 !important" }}
                              />
                            }
                            sx={{ p: 0 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#000",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {route.name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
                {Object.keys(routeDirections).length === 0 && (
                  <ListItem disableGutters dense>
                    <ListItemText
                      sx={{
                        px: 1,
                        py: 0.5,
                      }}
                      primary={
                        <Typography variant="body2" color="textSecondary">
                          No routes available
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        )}

        {/* Route info start point , end point */}
        {showRoute &&
          Object.values(routeDirections)
            .filter((route) => selectedRoutes.includes(route.name))
            .map((route, idx) => (
              <React.Fragment key={idx}>
                <DirectionsRenderer directions={route.result} />

                {/* START CIRCLE */}
                <OverlayView
                  position={route.origin}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    onMouseEnter={() => setHoveredPoint(`start-${idx}`)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#4CAF50",
                      borderRadius: "50%",
                      border: "2px solid white",
                      cursor: "pointer",
                    }}
                  />
                </OverlayView>

                {hoveredPoint === `start-${idx}` && (
                  <OverlayView
                    position={route.origin}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <Box
                      sx={{
                        background: "#fff",
                        p: 0.5,
                        borderRadius: 2,
                        boxShadow: 3,
                        minWidth: 160,
                        transform: "translateY(-100%)",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">
                        {route.name} start
                      </Typography>
                      <hr />
                      <Typography variant="caption">
                        Lat: {route.origin.lat.toFixed(4)}, Lng:{" "}
                        {route.origin.lng.toFixed(4)}
                      </Typography>
                    </Box>
                  </OverlayView>
                )}

                {/* END CIRCLE */}
                <OverlayView
                  position={route.destination}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    onMouseEnter={() => setHoveredPoint(`end-${idx}`)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: "#1A73E8",
                      borderRadius: "50%",
                      border: "2px solid white",
                      cursor: "pointer",
                    }}
                  />
                </OverlayView>

                {hoveredPoint === `end-${idx}` && (
                  <OverlayView
                    position={route.destination}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <Box
                      sx={{
                        background: "#fff",
                        p: 0.5,
                        borderRadius: 2,
                        boxShadow: 3,
                        minWidth: 160,
                        transform: "translateY(-100%)",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">
                        {route.name} end
                      </Typography>
                      <hr />
                      <Typography variant="caption">
                        Lat: {route.destination.lat.toFixed(4)}, Lng:{" "}
                        {route.destination.lng.toFixed(4)}
                      </Typography>
                    </Box>
                  </OverlayView>
                )}
              </React.Fragment>
            ))}

        {/* drawing marker trace  path*/}
        {activeUserRoutes.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              width: { xs: "calc(100% - 24px)", sm: 300 },
              maxWidth: 300,
              borderRadius: 2.25,
              overflow: "hidden",
              backgroundColor: "#5b5b5b",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(18px)",
              boxShadow: "0 16px 36px rgba(0,0,0,0.32)",
            }}
          >


            <Box
              sx={{
                p: 0.85,
                maxHeight: "calc(38vh - 44px)",
                overflowY: "auto",
              }}
            >
              {activeUserRoutes.map(({ user, path, start, end }, index) => {
                const status = userStatus[user.userName] || "playing";
                const progress = userProgress[user.userName] ?? 0;
                const isPaused = status === "paused";
                const isCompleted = status === "completed";
                const statusStyles = getPlaybackStatusStyles(status);
                return (
                  <Box
                    key={user.userName}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 0.75,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {user.userName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#ffffffb3",
                            fontSize: "10px",
                          }}
                        >

                        </Typography>

                      </Box>
                      <Box sx={{ display: "flex", gap: 0.75 }}>
                        <Tooltip
                          title={
                            isPaused || isCompleted
                              ? "Play route"
                              : "Pause route"
                          }
                        >
                          <IconButton
                            aria-label={
                              isPaused || isCompleted
                                ? `resume ${user.userName}`
                                : `pause ${user.userName}`
                            }
                            onClick={() => handlePausePlay(user.userName)}
                            sx={getPlaybackActionButtonSx(
                              isPaused || isCompleted
                                ? "rgba(56,189,248,0.18)"
                                : "rgba(37,99,235,0.24)",
                              isPaused || isCompleted
                                ? "rgba(56,189,248,0.28)"
                                : "rgba(37,99,235,0.34)",
                              "rgba(15,23,42,0.32)",
                            )}
                          >
                            {isPaused || isCompleted ? (
                              <PlayCircleOutlineIcon />
                            ) : (
                              <PauseCircleOutlineIcon />
                            )}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Restart route">
                          <IconButton
                            aria-label={`restart ${user.userName}`}
                            onClick={() => handleRestart(user.userName)}
                            sx={getPlaybackActionButtonSx(
                              "rgba(245,158,11,0.2)",
                              "rgba(245,158,11,0.32)",
                              "rgba(120,53,15,0.28)",
                            )}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Close route">
                          <IconButton
                            aria-label={`close ${user.userName}`}
                            onClick={() => handleRemove(user.userName)}
                            sx={getPlaybackActionButtonSx(
                              "rgba(239,68,68,0.2)",
                              "rgba(239,68,68,0.32)",
                              "rgba(127,29,29,0.28)",
                            )}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.2,
                          borderRadius: 99,
                          fontSize: "9px",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                          color: statusStyles.color,
                          backgroundColor: statusStyles.backgroundColor,
                          border: `1px solid ${statusStyles.borderColor}`,
                        }}
                      >
                        {getPlaybackStatusLabel(status)}
                      </Box>
                    </Box>
                    {(user._fromDate || user._toDate) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          mt: 0.4,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#ffffffb3", fontSize: "9.5px", lineHeight: 1.4 }}
                        >
                          {formatPayloadDate(user._fromDate)} &nbsp;&nbsp;

                          {formatRouteTime(start.timestamp)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "#ffffffb3", fontSize: "9.5px", lineHeight: 1.4, textAlign: "right" }}
                        >
                          {formatPayloadDate(user._toDate)}&nbsp;&nbsp;

                          {formatRouteTime(end.timestamp)}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        mt: 0.75,
                        height: 4,
                        borderRadius: 99,
                        overflow: "hidden",
                        backgroundColor: "rgba(255,255,255,0.12)",
                      }}
                    >

                      <Box
                        sx={{
                          width: `${progress}%`,
                          height: "100%",
                          borderRadius: 99,
                          background: isCompleted
                            ? "linear-gradient(90deg, #56CCF2 0%, #6FCF97 100%)"
                            : "linear-gradient(90deg, #58A6FF 0%, #8EECF5 100%)",
                          boxShadow: "0 0 18px rgba(88,166,255,0.38)",
                          transition: "width 180ms ease-out",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        mt: 0.55,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.68)",
                          fontSize: "10px",
                        }}
                      >
                        {progress}% complete
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.68)",
                          fontSize: "10px",
                        }}
                      >
                        {path.length} points
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {activeUserRoutes.map(({ user, path, start, end }) => (
          <React.Fragment key={user.userName}>
            <Polyline
              path={path}
              options={{
                strokeColor: "#FFFFFF",
                strokeOpacity: 0.92,
                strokeWeight: 8,
                zIndex: 4,
              }}
            />

            <Polyline
              path={path}
              options={{
                geodesic: true,
                strokeColor: "#1A73E8",
                strokeOpacity: 0.97,
                strokeWeight: 4.6,
                zIndex: 5,
              }}
              onMouseOver={(e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();

                const closest = path.reduce((prev, curr) =>
                  Math.hypot(curr.lat - lat, curr.lng - lng) <
                    Math.hypot(prev.lat - lat, prev.lng - lng)
                    ? curr
                    : prev,
                );

                setHoveredUser({
                  type: "route",
                  user: user.userName,
                  point: closest,
                });
              }}
              onMouseOut={() => setHoveredUser(null)}
            />

            {hoveredUser?.type === "route" &&
              hoveredUser?.user === user.userName && (
                <OverlayView
                  position={{
                    lat: hoveredUser.point.lat,
                    lng: hoveredUser.point.lng,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.96)",
                      color: "rgba(15,23,42,0.95)",
                      p: 0.5,
                      minWidth: 120,
                      borderRadius: 2,
                      boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
                      border: "1px solid rgba(15,23,42,0.08)",
                      transform: "translateY(-120%)",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <MDTypography variant="caption" color="inherit">
                      Time: {formatRouteTime(hoveredUser.point.timestamp)}
                    </MDTypography>
                  </Box>
                </OverlayView>
              )}

            <OverlayView
              position={start}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onMouseEnter={() =>
                  setHoveredUser({ type: "start", user: user.userName })
                }
                onMouseLeave={() => setHoveredUser(null)}
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: "#4CAF50",
                  borderRadius: "50%",
                  border: "2px solid white",
                  boxShadow: "0 0 0 4px rgba(76,175,80,0.18), 0 10px 18px rgba(15,23,42,0.18)",
                  cursor: "pointer",
                }}
              />
            </OverlayView>

            {hoveredUser?.type === "start" &&
              hoveredUser?.user === user.userName && (
                <OverlayView
                  position={start}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.96)",
                      color: "rgba(15,23,42,0.95)",
                      width: 156,
                      p: 1,
                      borderRadius: 2,
                      boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
                      border: "1px solid rgba(15,23,42,0.08)",
                      transform: "translateY(-120%)",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <Typography variant="caption" color="inherit">Start Point</Typography>
                    <hr />
                    <Typography variant="caption" color="inherit">
                      Time: {formatRouteTime(start.timestamp)}
                    </Typography>
                    <hr />
                    <Typography variant="caption" color="inherit">
                      Lat: {start.lat.toFixed(5)}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                      Lng: {start.lng.toFixed(5)}
                    </Typography>
                  </Box>
                </OverlayView>
              )}

            <OverlayView
              position={end}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onMouseEnter={() =>
                  setHoveredUser({ type: "end", user: user.userName })
                }
                onMouseLeave={() => setHoveredUser(null)}
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: "#1A73E8",
                  borderRadius: "50%",
                  border: "2px solid white",
                  boxShadow: "0 0 0 4px rgba(26,115,232,0.16), 0 10px 18px rgba(15,23,42,0.18)",
                  cursor: "pointer",
                }}
              />
            </OverlayView>

            {hoveredUser?.type === "end" &&
              hoveredUser?.user === user.userName && (
                <OverlayView
                  position={end}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.96)",
                      color: "rgba(15,23,42,0.95)",
                      p: 1,
                      width: 156,
                      borderRadius: 2,
                      boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
                      border: "1px solid rgba(15,23,42,0.08)",
                      transform: "translateY(-120%)",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <Typography variant="caption" color="inherit">End Point</Typography>
                    <hr />
                    <Typography variant="caption" color="inherit">
                      Time: {formatRouteTime(end.timestamp)}
                    </Typography>
                    <hr />
                    <Typography variant="caption" color="inherit">
                      Lat: {end.lat.toFixed(5)}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                      Lng: {end.lng.toFixed(5)}
                    </Typography>
                  </Box>
                </OverlayView>
              )}
          </React.Fragment>
        ))}
      </GoogleMap>
      <CTLNotification notify={notify} setNotify={setNotify} />
      <GeofenceEditorDialog
        open={geofenceEditorOpen}
        shape={pendingGeofenceShape}
        geofence={editingGeofence}
        onColorChange={handleGeofenceColorPreview}
        onClose={() => {
          clearPendingGeofenceOverlay();
          setGeofenceEditorOpen(false);
          setPendingGeofenceShape(null);
          setEditingGeofence(null);
        }}
        onSaved={() => {
          clearPendingGeofenceOverlay();
          setGeofenceEditorOpen(false);
          setPendingGeofenceShape(null);
          setEditingGeofence(null);
        }}
      />
    </LoadScript>
  );
};

export default MapComponent;
